import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertCircle,
  BarChart3,
  Building2,
  Camera,
  Clock3,
  ClipboardList,
  Globe2,
  Image as ImageIcon,
  Link as LinkIcon,
  Loader2,
  MapPin,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Trash2,
  Upload,
  Filter,
  RefreshCw,
  Repeat2,
  Search,
  ShoppingCart,
  Store,
  UserPlus,
  Users,
  X,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { StudioPurchaseModal, type StudioPurchaseMode } from '@/components/ownership/StudioPurchaseModal'
import { StudioOnboardingModal, type StudioOnboardingMode } from '@/components/ownership/StudioOnboardingModal'
import { StudioStateChangeModal, type StudioStateChangeMode } from '@/components/ownership/StudioStateChangeModal'
import { PartnerStatusModal, type PartnerStatusMode } from '@/components/ownership/PartnerStatusModal'
import {
  PartnerGroupMembershipModal,
  type PartnerGroupMembershipMode,
} from '@/components/ownership/PartnerGroupMembershipModal'
import { StudioSubscriptionModelModal } from '@/components/ownership/StudioSubscriptionModelModal'
import {
  deletePartnerPersonPhoto,
  linkPartnerPersonPhoto,
  listPartnerPhotoPeople,
  type PartnerPhotoPerson,
  uploadPartnerPersonPhoto,
} from '@/lib/partnerPhotos'
import { useSuperAdmin } from '@/hooks/useSuperAdmin'
import { isOriginalCaseTested } from '@/lib/originalCaseStatus'
import {
  createOriginalChangeCase,
  type OriginalChangeCaseAttachment,
  type OriginalChangeCaseEventType,
} from '@/services/originalChangeCaseService'
import {
  fetchOnboardingCities,
  fetchLandGroups,
  fetchCityTimezones,
  fetchPopulationGroups,
  type LandGroup,
  type OnboardingCity,
  type PopulationGroup,
} from '@/services/studioOnboardingService'

type OriginalHistoryRow = Record<string, unknown>
type SortDirection = 'asc' | 'desc'
type SortState = {
  column: string
  direction: SortDirection
}
type DateFilterMode = 'before' | 'after' | 'between'
type DateFilterState = {
  mode: DateFilterMode
  from: string
  to: string
}
type ColumnMenuPosition = {
  top: number
  left: number
  maxHeight: number
}
type OriginalHistoryModeId = 'history' | 'discovery' | 'city' | 'partners' | 'statistics'
type OriginalHistoryMode = {
  id: OriginalHistoryModeId
  label: string
  description?: string
  rpc: string
  schema?: 'public' | 'reporting'
  icon: typeof Store
  columns?: string[]
  rowFilter?: 'active' | 'discovery'
  excludeColumn?: (column: string) => boolean
}
type OriginalCaseAction =
  | { type: 'onboarding'; onboardingMode: StudioOnboardingMode; partnerTarget?: 'new_studio' | 'buy_studio' }
  | { type: 'purchase'; purchaseMode: StudioPurchaseMode }
  | { type: 'state_change'; stateChangeMode: StudioStateChangeMode }
  | { type: 'partner_status'; partnerStatusMode: PartnerStatusMode }
  | { type: 'partner_group'; partnerGroupMode: PartnerGroupMembershipMode }
  | { type: 'subscription_model' }

type OriginalCaseItem = {
  label: string
  description: string
  action: OriginalCaseAction
}

type OriginalCaseCategory = {
  title: string
  description: string
  icon: typeof UserPlus
  items: OriginalCaseItem[]
}

type OriginalSelectedStudio = {
  selectionKey: string
  studioInternalId: number
  studioNumber: string
  cityName: string
  status: string
  partnerName: string
  partnerNumber: number | null
}

type InformerEventConfig = {
  type: OriginalChangeCaseEventType
  label: string
  targetStatus: string | null
  supportsBulk: boolean
  description: string
}

type ReportingRpcClient = {
  schema: (schemaName: 'public' | 'reporting') => {
    rpc: (rpcName: string) => Promise<{
      data: unknown
      error: { message?: string } | null
    }>
  }
}

const DISPLAY_COLUMNS = [
  'Внутренний номер студии',
  '№ студии',
  'Старый номер',
  'Город',
  'Земли',
  'МО или ЛО?',
  'Население, тыс.чел',
  'Страна',
  'Дата открытия',
  'Статус',
  'Статус владения',
  'Франчайзи',
  'Номер франчайзи',
  'Статус франчайзи',
  'Дата внесения взноса',
  'Дата начала владения',
  'Дата закрытия/отмены/перепродажи',
  'Кто продал',
  'Кому продал',
  'Число залов в студии:',
  'Площадь cтудии:',
  'Адрес студии',
  'Тип подписки',
]

const ORIGINAL_COLUMN = {
  studioInternalId: 'Внутренний номер студии',
  studioNumber: '№ студии',
  city: 'Город',
  openDate: 'Дата открытия',
  studioStatus: 'Статус',
  ownershipStatus: 'Статус владения',
  partner: 'Франчайзи',
  partnerNumber: 'Номер франчайзи',
  contributionDate: 'Дата внесения взноса',
  ownershipStartDate: 'Дата начала владения',
  ownershipEndDate: 'Дата закрытия/отмены/перепродажи',
} as const

const ORIGINAL_PARTNER_COLUMNS = [
  'Франчайзи',
  'Номер франчайзи',
  'Статус франчайзи',
  'Город',
  'Страна',
  'Студий у франчайзи открывается',
  'Студий у франчайзи открыто',
]

const COLUMN_ALIASES: Record<string, string[]> = {
  'Внутренний номер студии': ['studio_internal_id', 'Внутренний номер студии', 'Внутр. ID (если нет №)'],
  'Внутр. ID (если нет №)': ['studio_internal_id', 'Внутренний номер студии', 'Внутр. ID (если нет №)'],
  Статус: ['Статус', 'Статус студии', 'status', 'status_code'],
  'Статус владения': ['Статус владения', 'ownership_phase'],
  'Площадь cтудии:': ['Площадь cтудии:', 'Площадь студии:', 'area'],
  'Тип подписки': ['Тип подписки', 'subscription_type'],
}

const PARTNER_COLUMN_ALIASES: Record<string, string[]> = {
  Франчайзи: ['Партнер', 'Партнёр', 'persons_fio', 'partner_name', 'partner'],
  'Номер франчайзи': ['Номер партнера', 'Номер партнёра', 'partner_number', 'partner_id'],
  'Статус франчайзи': [
    'Статус партнера',
    'Статус партнёра',
    'last_partner_status',
    'partner_status',
    'status',
    'status_code',
  ],
  Город: ['cities', 'city_name', 'cities_text', 'city'],
  Страна: ['countries', 'country', 'country_name'],
  'Студий у франчайзи открывается': [
    'Студий у партнера открывается',
    'Студий у партнёра открывается',
    'studios_opening',
    'studios_opening_count',
    'opening_studios_count',
    'preopen_studios_count',
    'planned_studios_count',
    'opening_count',
  ],
  'Студий у франчайзи открыто': [
    'Студий у партнера открыто',
    'Студий у партнёра открыто',
    'studios_opened',
    'open_studios_count',
    'opened_studios_count',
    'studios_open_count',
    'open_count',
  ],
}

const ORIGINAL_HISTORY_MODES: OriginalHistoryMode[] = [
  {
    id: 'discovery',
    label: 'Активные',
    description: 'Все активные студии и владельцы сети в текущее время',
    rpc: 'original_history_int1',
    icon: ClipboardList,
    columns: DISPLAY_COLUMNS,
    rowFilter: 'discovery',
  },
  {
    id: 'history',
    label: 'История',
    description: 'Все студии и владельцы за всю историю сети.',
    rpc: 'original_history_int1',
    icon: Store,
    columns: DISPLAY_COLUMNS,
  },
  {
    id: 'city',
    label: 'Город',
    description: 'Города, в которых есть или были студии сети',
    rpc: 'original_city',
    icon: Store,
    excludeColumn: isCityHiddenColumn,
  },
  {
    id: 'partners',
    label: 'Франчайзи',
    description: 'Франчайзи, которые есть или были в сети',
    rpc: 'original_partners',
    icon: Users,
    columns: ORIGINAL_PARTNER_COLUMNS,
  },
  {
    id: 'statistics',
    label: 'Статистика',
    rpc: 'get_global_statistics_new',
    icon: BarChart3,
    excludeColumn: isGlobalStatisticsHiddenColumn,
  },
]

const ORIGINAL_SEARCH_COLUMNS: Record<Exclude<OriginalHistoryModeId, 'statistics'>, string[]> = {
  discovery: [
    ORIGINAL_COLUMN.studioNumber,
    ORIGINAL_COLUMN.city,
    ORIGINAL_COLUMN.studioStatus,
    ORIGINAL_COLUMN.ownershipStatus,
    ORIGINAL_COLUMN.partner,
    ORIGINAL_COLUMN.partnerNumber,
    'Страна',
  ],
  history: [
    ORIGINAL_COLUMN.studioNumber,
    ORIGINAL_COLUMN.city,
    ORIGINAL_COLUMN.studioStatus,
    ORIGINAL_COLUMN.ownershipStatus,
    ORIGINAL_COLUMN.partner,
    ORIGINAL_COLUMN.partnerNumber,
    'Страна',
  ],
  city: ['Город', 'Страна', 'Земля'],
  partners: [
    'Франчайзи',
    'Номер франчайзи',
    'Статус франчайзи',
    'Город',
    'Страна',
  ],
}

const ORIGINAL_SEARCH_PLACEHOLDERS: Record<Exclude<OriginalHistoryModeId, 'statistics'>, string> = {
  discovery: 'Номер студии, город, франчайзи, статус…',
  history: 'Номер студии, город, франчайзи, статус…',
  city: 'Город, страна, земля…',
  partners: 'ФИО, номер франчайзи, город, статус…',
}

const GLOBAL_STATISTICS_METRIC_COLUMN = 'Показатель'
const GLOBAL_STATISTICS_VALUE_COLUMN = 'Значение'

const GLOBAL_STATISTICS_METRIC_RENAME: Record<string, string> = {
  'Кол-во партнёров владеет/на продаже': 'Кол-во франчайзи владеет/на продаже',
  'Городов присутствия': 'Городов присутствия',
  'Кол-во партнёров зашли на покупку': '   Кол-во франчайзи зашли на покупку студий',
  'Кол-во партнёров открывают с 0': '   Кол-во франчайзи открывают с 0 студий',
  'Открыто по франшизе всего (OPEN + SERVICE)': '   Открыто по франшизе всего',
  'Открыто филиалов (партнёр 1000)': '   Открыто филиалов',
  'Кол-во активных партнёров': 'Кол-во активных франчайзи',
  'Стран присутствия': 'Стран присутствия',
  'Студий работает на выхаживаемость (SERVICE)': '      Студий без продаж',
  'Студий открывается (PRE-OPEN)': 'Студий открывается',
  'Студий открыто всего (OPEN + SERVICE)': 'Студий открыто всего',
  'Кол-во партнёров оплатили пауш': '   Кол-во франчайзи оплатили квоту',
  'Открыто по франшизе (только OPEN)': '      Открыто по франшизе (активных)',
  'Кол-во партнёров с приостановленными студиями': 'Кол-во франчайзи с приостановленными студиями',
  'Кол-во партнёров с закрытыми студиями': 'Кол-во франчайзи с закрытыми студиями',
  'Кол-во партнёров в открытии': 'Кол-во франчайзи в открытии',
  'Городов открыто': '   Городов открыто',
  'Городов открывается': '   Городов открывается',
  'Количество приостановленных студий': 'Количество приостановленных студий',
}

const GLOBAL_STATISTICS_METRIC_ORDER = [
  'Стран присутствия',
  'Городов присутствия',
  '   Городов открыто',
  '   Городов открывается',
  'Студий открыто всего',
  '   Открыто по франшизе всего',
  '      Открыто по франшизе (активных)',
  '      Студий без продаж',
  '   Открыто филиалов',
  'Студий открывается',
  'Количество приостановленных студий',
  'Кол-во активных франчайзи',
  'Кол-во франчайзи в открытии',
  '   Кол-во франчайзи зашли на покупку студий',
  '   Кол-во франчайзи открывают с 0 студий',
  'Кол-во франчайзи владеет/на продаже',
  '   Кол-во франчайзи оплатили квоту',
  'Кол-во франчайзи с приостановленными студиями',
  'Кол-во франчайзи с закрытыми студиями',
]

const GLOBAL_STATISTICS_METRIC_ORDER_INDEX = new Map(
  GLOBAL_STATISTICS_METRIC_ORDER.map((metric, index) => [metric, index])
)

type StatisticsDashboardItem = {
  label: string
  match: string
}

type StatisticsDashboardGroup = {
  title: string
  description: string
  accent: 'blue' | 'orange' | 'green'
  icon: typeof Globe2
  items: StatisticsDashboardItem[]
}

const STATISTICS_DASHBOARD_GROUPS: StatisticsDashboardGroup[] = [
  {
    title: 'География',
    description: 'Страны и города присутствия',
    accent: 'blue',
    icon: Globe2,
    items: [
      { label: 'Стран присутствия', match: 'Стран присутствия' },
      { label: 'Городов присутствия', match: 'Городов присутствия' },
      { label: 'Городов открыто', match: 'Городов открыто' },
      { label: 'Городов открывается', match: 'Городов открывается' },
    ],
  },
  {
    title: 'Студии',
    description: 'Открытия, форматы и статусы',
    accent: 'orange',
    icon: Store,
    items: [
      { label: 'Студий открыто всего', match: 'Студий открыто всего' },
      { label: 'Открыто по франшизе всего', match: 'Открыто по франшизе всего' },
      { label: 'Открыто по франшизе (активных)', match: 'Открыто по франшизе (активных)' },
      { label: 'Студий без продаж', match: 'Студий без продаж' },
      { label: 'Открыто филиалов', match: 'Открыто филиалов' },
      { label: 'Студий открывается', match: 'Студий открывается' },
      { label: 'Количество приостановленных студий', match: 'Количество приостановленных студий' },
    ],
  },
  {
    title: 'Франчайзи',
    description: 'Партнёрская сеть и воронка',
    accent: 'green',
    icon: Users,
    items: [
      { label: 'Кол-во активных франчайзи', match: 'Кол-во активных франчайзи' },
      { label: 'Кол-во франчайзи в открытии', match: 'Кол-во франчайзи в открытии' },
      { label: 'Кол-во франчайзи зашли на покупку студий', match: 'Кол-во франчайзи зашли на покупку студий' },
      { label: 'Кол-во франчайзи открывают с 0 студий', match: 'Кол-во франчайзи открывают с 0 студий' },
      { label: 'Кол-во франчайзи владеет/на продаже', match: 'Кол-во франчайзи владеет/на продаже' },
      { label: 'Кол-во франчайзи оплатили квоту', match: 'Кол-во франчайзи оплатили квоту' },
      { label: 'Кол-во франчайзи с приостановленными студиями', match: 'Кол-во франчайзи с приостановленными студиями' },
      { label: 'Кол-во франчайзи с закрытыми студиями', match: 'Кол-во франчайзи с закрытыми студиями' },
    ],
  },
]

const STATISTICS_DASHBOARD_SUMMARY: StatisticsDashboardItem[] = [
  { label: 'Стран присутствия', match: 'Стран присутствия' },
  { label: 'Городов присутствия', match: 'Городов присутствия' },
  { label: 'Студий открыто', match: 'Студий открыто всего' },
  { label: 'Студий открывается', match: 'Студий открывается' },
  { label: 'Активных франчайзи', match: 'Кол-во активных франчайзи' },
  { label: 'Франчайзи в открытии', match: 'Кол-во франчайзи в открытии' },
]

const STATUS_MAP: Record<string, string> = {
  CLOSED: 'закрыта',
  OPEN: 'открыта',
  SUSPENDED: 'временно приостановлена',
  CANCELLED: 'отменена',
  'PRE-OPEN': 'открывается',
  SWITCH_TO_PURCHASE: 'переход на покупку',
  SERVICE: 'нет продаж',
}

const OWNERSHIP_MAP: Record<string, string> = {
  BUYING: 'зашел на покупку',
  OWNED: 'владеет',
  FOR_SALE: 'на продаже',
  PLANNED: 'планируется к открытию',
  CLOSED: 'закрыта',
  SOLD: 'продана',
  TRANSFERRED: 'передана УК',
  NOT_OPENED: 'не открылась',
}

const SUBSCRIPTION_MAP: Record<string, string> = {
  REGULAR: 'абонементы',
  SUBSCRIPTION: 'подписки',
  MIXED: 'смешанный',
}

const ACTIVE_ORIGINAL_STATUSES = new Set(['OPEN', 'PRE-OPEN', 'SERVICE'])
const COUNTRY_PRESENCE_STATUSES = new Set(['OPEN', 'SERVICE'])
const DISCOVERY_OWNERSHIP_PHASES = new Set(['PLANNED', 'BUYING'])
const EXCLUDED_INFORMER_EVENT_TYPES = new Set<OriginalChangeCaseEventType>([
  // Модель студии ведёт договор; ручная отметка — прямое действие advanced, а не кейс.
  'SUBSCRIPTION_MANUAL_MODEL',
  'ONBOARDING_PARTNER_NEW_STUDIO',
  'ONBOARDING_STUDIO',
  'PARTNER_STATUS_TERMINATION',
])

const NARROW_COLUMNS = new Set([
  'Внутренний номер студии',
  '№ студии',
  'Старый номер',
  'Число залов в студии:',
  'Площадь cтудии:',
])

const ORIGINAL_CASE_CATEGORIES: OriginalCaseCategory[] = [
  {
    title: 'Добавить франчайзи или студию',
    description: 'Новый франчайзи, новая студия или заготовка студии без договора.',
    icon: Building2,
    items: [
      {
        label: 'Новый франчайзи на покупку',
        description: 'Завести франчайзи и владение BUYING на существующую студию.',
        action: { type: 'onboarding', onboardingMode: 'partner', partnerTarget: 'buy_studio' },
      },
      {
        label: 'Новый франчайзи на новую студию',
        description: 'Завести франчайзи, серию и студию-заготовку в PLANNED.',
        action: { type: 'onboarding', onboardingMode: 'partner', partnerTarget: 'new_studio' },
      },
      {
        label: 'Планирование студии франчайзи',
        description: 'Создать студию-заготовку существующему франчайзи.',
        action: { type: 'onboarding', onboardingMode: 'studio' },
      },
      {
        label: 'Регистрация студии и присвоение номера',
        description: 'Присвоить номер и адрес студии-заготовке.',
        action: { type: 'onboarding', onboardingMode: 'register' },
      },
      {
        label: 'Фактическое открытие студии',
        description: 'Открыть уже запланированную и зарегистрированную студию.',
        action: { type: 'onboarding', onboardingMode: 'open' },
      },
    ],
  },
  {
    title: 'Покупка или продажа студии',
    description: 'Покупатель, продавец, дата передачи и смена владения.',
    icon: ShoppingCart,
    items: [
      {
        label: 'Планирование покупки',
        description: 'Создать BUYING без даты старта и без финансового эффекта.',
        action: { type: 'purchase', purchaseMode: 'plan' },
      },
      {
        label: 'Проведение покупки/продажи',
        description: 'Закрыть продавца и поднять покупателя с даты передачи.',
        action: { type: 'purchase', purchaseMode: 'complete' },
      },
    ],
  },
  {
    title: 'Изменить состояние студии',
    description: 'Открытие, пауза, закрытие, отмена или переход в УК.',
    icon: Store,
    items: [
      {
        label: 'Студия возобновила работу',
        description: 'Вернуть студию в статус открыта после приостановки или режима нет продаж.',
        action: { type: 'state_change', stateChangeMode: 'resume_studio' },
      },
      {
        label: 'Студия приостановлена',
        description: 'Временно остановить работу с проверкой текущего статуса.',
        action: { type: 'state_change', stateChangeMode: 'suspend_studio' },
      },
      {
        label: 'Студия нет продаж',
        description: 'Перевести студию в сервисный статус без продаж.',
        action: { type: 'state_change', stateChangeMode: 'service_status' },
      },
      {
        label: 'Студия закрыта',
        description: 'Закрыть студию и связанные активные офферы.',
        action: { type: 'state_change', stateChangeMode: 'close_studio' },
      },
      {
        label: 'Студия отменена',
        description: 'Отметить, что студия не открылась. Внутри: сначала по студии, затем по франчайзи.',
        action: { type: 'state_change', stateChangeMode: 'cancel_studio' },
      },
      {
        label: 'Студия перешла в УК',
        description: 'Передать студию управляющей компании, включая 1000 франчайзи, и открыть оффер.',
        action: { type: 'state_change', stateChangeMode: 'transfer_to_management' },
      },
      {
        label: 'Смена адреса студии',
        description: 'Обновить адрес действующей или планируемой студии.',
        action: { type: 'state_change', stateChangeMode: 'change_address' },
      },
    ],
  },
  {
    title: 'Продажа студии как оффер',
    description: 'Намерение продажи отдельно от фактической передачи владения.',
    icon: ClipboardList,
    items: [
      {
        label: 'Студия выставлена на продажу',
        description: 'Создать активный оффер продажи.',
        action: { type: 'state_change', stateChangeMode: 'sale_offer' },
      },
      {
        label: 'Студия сменила оффер',
        description: 'Обновить условия активного оффера.',
        action: { type: 'state_change', stateChangeMode: 'update_sale_offer' },
      },
      {
        label: 'Студия закрыла оффер',
        description: 'Снять студию с продажи без передачи владения.',
        action: { type: 'state_change', stateChangeMode: 'cancel_sale_offer' },
      },
    ],
  },
  {
    title: 'Франчайзи и люди',
    description: 'Статусы франчайзи и состав группы франчайзи.',
    icon: Users,
    items: [
      {
        label: 'Франчайзи бывший',
        description: 'Перевести франчайзи в бывшие с нужной даты.',
        action: { type: 'partner_status', partnerStatusMode: 'former' },
      },
      {
        label: 'Франчайзи на расторжении',
        description: 'Отметить ожидание подписанного расторжения.',
        action: { type: 'partner_status', partnerStatusMode: 'termination' },
      },
      {
        label: 'Добавление персоны в группу франчайзи',
        description: 'Добавить человека в действующий состав франчайзи.',
        action: { type: 'partner_group', partnerGroupMode: 'add_person' },
      },
      {
        label: 'Выход персоны из группы',
        description: 'Закрыть участие человека в группе франчайзи.',
        action: { type: 'partner_group', partnerGroupMode: 'end_person' },
      },
      {
        label: 'Форма имени в списках',
        description:
          'Как показывать человека в списках, ШМП и рейтингах, плюс имя его аккаунта. Юридическое ФИО меняют юристы в реестре документов.',
        action: { type: 'partner_group', partnerGroupMode: 'display_name' },
      },
    ],
  },
  {
    title: 'Модель продаж студии',
    description: 'Абонементы, подписки и гибрид задаются условиями договора; здесь — только исключения.',
    icon: Repeat2,
    items: [
      {
        label: 'Модель студии вне договора',
        description:
          'Ручная отметка для студий, где модель не следует из договора (подписки по абонементному договору, студия УК). Переход по договору — новым интервалом условий на странице серии.',
        action: { type: 'subscription_model' },
      },
    ],
  },
]

function slugifyOriginalCase(value: string) {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase()
}

function getInformerEventMeta(action: OriginalCaseAction): Pick<InformerEventConfig, 'type' | 'targetStatus' | 'supportsBulk'> {
  if (action.type === 'state_change') {
    const stateChangeMeta: Record<StudioStateChangeMode, Pick<InformerEventConfig, 'type' | 'targetStatus' | 'supportsBulk'>> = {
      resume_studio: { type: 'RESUMED', targetStatus: 'OPEN', supportsBulk: true },
      suspend_studio: { type: 'SUSPENDED', targetStatus: 'SUSPENDED', supportsBulk: true },
      service_status: { type: 'NO_SALES', targetStatus: 'SERVICE', supportsBulk: true },
      close_studio: { type: 'CLOSED', targetStatus: 'CLOSED', supportsBulk: true },
      cancel_studio: { type: 'STATE_CHANGE_CANCEL_STUDIO', targetStatus: 'CANCELLED', supportsBulk: false },
      transfer_to_management: { type: 'STATE_CHANGE_TRANSFER_TO_MANAGEMENT', targetStatus: 'TRANSFERRED', supportsBulk: false },
      change_address: { type: 'STATE_CHANGE_CHANGE_ADDRESS', targetStatus: null, supportsBulk: false },
      sale_offer: { type: 'STATE_CHANGE_SALE_OFFER', targetStatus: 'FOR_SALE', supportsBulk: false },
      update_sale_offer: { type: 'STATE_CHANGE_UPDATE_SALE_OFFER', targetStatus: null, supportsBulk: false },
      cancel_sale_offer: { type: 'SALE_OFFER_CLOSED', targetStatus: null, supportsBulk: false },
    }

    return stateChangeMeta[action.stateChangeMode]
  }

  if (action.type === 'onboarding') {
    const suffix = action.onboardingMode === 'partner'
      ? `${action.onboardingMode}_${action.partnerTarget ?? 'new_studio'}`
      : action.onboardingMode
    return {
      type: `ONBOARDING_${slugifyOriginalCase(suffix)}`,
      targetStatus: action.onboardingMode === 'open' ? 'OPEN' : null,
      supportsBulk: false,
    }
  }

  if (action.type === 'purchase') {
    return {
      type: `PURCHASE_${slugifyOriginalCase(action.purchaseMode)}`,
      targetStatus: action.purchaseMode === 'complete' ? 'SOLD' : 'BUYING',
      supportsBulk: false,
    }
  }

  if (action.type === 'partner_status') {
    return {
      type: `PARTNER_STATUS_${slugifyOriginalCase(action.partnerStatusMode)}`,
      targetStatus: slugifyOriginalCase(action.partnerStatusMode),
      supportsBulk: false,
    }
  }

  if (action.type === 'partner_group') {
    return {
      type: `PARTNER_GROUP_${slugifyOriginalCase(action.partnerGroupMode)}`,
      targetStatus: null,
      supportsBulk: false,
    }
  }

  if (action.type === 'subscription_model') {
    return {
      type: 'SUBSCRIPTION_MANUAL_MODEL',
      targetStatus: null,
      supportsBulk: false,
    }
  }

  throw new Error('Неизвестный тип действия исходного кейса')
}

const INFORMER_EVENT_CONFIGS: InformerEventConfig[] = ORIGINAL_CASE_CATEGORIES.flatMap(category =>
  category.items.map(item => {
    const meta = getInformerEventMeta(item.action)
    return {
      ...meta,
      label: item.label,
      description: item.description,
    }
  })
).filter(item => !EXCLUDED_INFORMER_EVENT_TYPES.has(item.type))

const TRANSFERABLE_INFORMER_EVENT_CONFIGS = INFORMER_EVENT_CONFIGS.filter(
  item => item.type !== 'CLOSED' && item.type !== 'NO_SALES'
)

function toRows(payload: unknown): OriginalHistoryRow[] {
  if (Array.isArray(payload)) {
    return payload.filter((row): row is OriginalHistoryRow => row !== null && typeof row === 'object' && !Array.isArray(row))
  }

  if (payload !== null && typeof payload === 'object') {
    const objectPayload = payload as Record<string, unknown>
    const nestedRows = Object.values(objectPayload).find(Array.isArray)
    if (nestedRows) return toRows(nestedRows)
  }

  return []
}

function normalizeGlobalStatisticsMetric(value: unknown) {
  const text = String(value ?? '').trim()
  return GLOBAL_STATISTICS_METRIC_RENAME[text] ?? text
}

function normalizeGlobalStatisticsColumn(column: string) {
  const normalized = normalizeColumnName(column)
  if (normalized === 'значение_сейчас' || normalized === 'znachenie_seychas') {
    return GLOBAL_STATISTICS_VALUE_COLUMN
  }

  return column
}

function normalizeGlobalStatisticsRows(rows: OriginalHistoryRow[]) {
  const metricColumn = Object.keys(rows[0] ?? {})[0]
  if (!metricColumn) return rows

  return rows
    .map((row, index) => {
      const nextRow: OriginalHistoryRow = {
        [GLOBAL_STATISTICS_METRIC_COLUMN]: normalizeGlobalStatisticsMetric(row[metricColumn]),
      }

      for (const [key, value] of Object.entries(row)) {
        if (key === metricColumn || key === GLOBAL_STATISTICS_METRIC_COLUMN) continue
        nextRow[normalizeGlobalStatisticsColumn(key)] = value
      }

      return { row: nextRow, index }
    })
    .sort((left, right) => {
      const leftMetric = String(left.row[GLOBAL_STATISTICS_METRIC_COLUMN] ?? '')
      const rightMetric = String(right.row[GLOBAL_STATISTICS_METRIC_COLUMN] ?? '')
      const leftOrder = GLOBAL_STATISTICS_METRIC_ORDER_INDEX.get(leftMetric) ?? Number.MAX_SAFE_INTEGER
      const rightOrder = GLOBAL_STATISTICS_METRIC_ORDER_INDEX.get(rightMetric) ?? Number.MAX_SAFE_INTEGER

      return leftOrder === rightOrder ? left.index - right.index : leftOrder - rightOrder
    })
    .map(item => item.row)
}

function normalizeColumnName(column: string) {
  return column.toLocaleLowerCase('ru').replace(/\s+/g, ' ').trim()
}

function isGlobalStatisticsHiddenColumn(column: string) {
  const normalized = normalizeColumnName(column)
  return normalized === 'разница'
    || normalized === 'difference'
    || (
      normalized.includes('7')
      && (
      normalized.includes('данные 7 дней назад')
      || normalized.includes('7 дней назад')
      || normalized.includes('значение_7_дней_нада')
      || normalized.includes('значение_7_дней_назад')
      || normalized.includes('znachenie_7_dney_nazad')
      || normalized.includes('7 days ago')
    )
  )
}

function isCityHiddenColumn(column: string) {
  return normalizeColumnName(column) === 'city_id'
}

function StatisticsDashboard({ rows, gradeRows, specialCaseRows, countryRows }: {
  rows: OriginalHistoryRow[]
  gradeRows: OriginalHistoryRow[]
  specialCaseRows: OriginalHistoryRow[]
  countryRows: OriginalHistoryRow[]
}) {
  const valueByMetric = useMemo(() => {
    const values = new Map<string, OriginalHistoryRow>()

    for (const row of rows) {
      const metric = String(row[GLOBAL_STATISTICS_METRIC_COLUMN] ?? '').trim()
      if (metric) values.set(metric, row)
    }

    return values
  }, [rows])

  const getValue = (match: string) => {
    const metric = Array.from(valueByMetric.keys()).find(key => key === match || key.includes(match))
    return metric ? formatCellValue(valueByMetric.get(metric)?.[GLOBAL_STATISTICS_VALUE_COLUMN]) : '—'
  }

  const getDifference = (match: string) => {
    const metric = Array.from(valueByMetric.keys()).find(key => key === match || key.includes(match))
    const row = metric ? valueByMetric.get(metric) : undefined
    const differenceEntry = Object.entries(row ?? {}).find(([key]) => {
      const normalized = normalizeColumnName(key)
      return normalized === 'разница' || normalized === 'difference'
    })
    const difference = Number(differenceEntry?.[1])
    return Number.isFinite(difference) ? difference : null
  }

  const summaryIcons = [Globe2, MapPin, Store, Clock3, Users, Building2]
  const accentClasses = {
    blue: {
      panel: 'border-primary/30 bg-gradient-to-br from-card via-card to-primary/15',
      icon: 'bg-primary/10 text-primary',
      value: 'text-primary',
    },
    orange: {
      panel: 'border-primary/30 bg-gradient-to-br from-card via-card to-primary/15',
      icon: 'bg-primary/10 text-primary',
      value: 'text-primary',
    },
    green: {
      panel: 'border-primary/30 bg-gradient-to-br from-card via-card to-primary/15',
      icon: 'bg-primary/10 text-primary',
      value: 'text-primary',
    },
  } as const

  return (
    <div className="flex items-start gap-4 overflow-x-auto pb-1">
      <section className="w-full shrink-0 space-y-4 rounded-xl border border-border bg-muted/20 p-3 sm:p-4" style={{ maxWidth: 760 }} aria-label="Статистика сети">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {STATISTICS_DASHBOARD_SUMMARY.map((item, index) => {
          const Icon = summaryIcons[index]
          const difference = getDifference(item.match)

          return (
            <div key={item.match} className="min-h-[132px] rounded-xl border border-border bg-card p-3 shadow-sm">
              <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Icon size={19} />
              </div>
              <div className="min-h-9 text-xs font-medium leading-4 text-muted-foreground">{item.label}</div>
              <div className="mt-1 flex items-baseline gap-2">
                <div className="text-2xl font-bold tabular-nums text-foreground">{getValue(item.match)}</div>
                {difference !== null && difference !== 0 && (
                  <div className={`text-xs font-semibold tabular-nums ${difference > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {difference > 0 ? '↑ +' : '↓ '}{difference}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <div className="grid grid-cols-1 items-stretch gap-4 xl:grid-cols-3">
        {STATISTICS_DASHBOARD_GROUPS.map(group => {
          const Icon = group.icon
          const styles = accentClasses[group.accent]

          return (
            <article key={group.title} className={`flex min-h-[390px] flex-col rounded-xl border p-4 ${styles.panel}`}>
              <header className="mb-3 flex items-center gap-3 border-b border-current/10 pb-3">
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${styles.icon}`}>
                  <Icon size={23} />
                </div>
                <div>
                  <h2 className="text-base font-bold text-foreground">{group.title}</h2>
                  <p className="mt-0.5 text-xs text-muted-foreground">{group.description}</p>
                </div>
              </header>
              <div className="flex-1 divide-y divide-current/10">
                {group.items.map(item => (
                  <div key={item.match} className="flex min-h-[48px] items-center justify-between gap-3 py-2.5">
                    <span className="text-sm leading-5 text-foreground">{item.label}</span>
                    <span className={`shrink-0 text-lg font-bold tabular-nums ${styles.value}`}>{getValue(item.match)}</span>
                  </div>
                ))}
              </div>
            </article>
          )
        })}
      </div>

      </section>
      <StatisticsAdditionalPanels gradeRows={gradeRows} specialCaseRows={specialCaseRows} countryRows={countryRows} />
    </div>
  )
}

function getRowField(row: OriginalHistoryRow, candidates: string[]) {
  const entry = Object.entries(row).find(([key]) => {
    const normalized = normalizeColumnName(key)
    return candidates.some(candidate => normalized === candidate || normalized.includes(candidate))
  })
  return entry?.[1]
}

function pluralizeRussian(value: number, one: string, few: string, many: string) {
  const absolute = Math.abs(value) % 100
  if (absolute >= 11 && absolute <= 19) return many
  switch (absolute % 10) {
    case 1: return one
    case 2:
    case 3:
    case 4: return few
    default: return many
  }
}

function StatisticsAdditionalPanels({ gradeRows, specialCaseRows, countryRows }: {
  gradeRows: OriginalHistoryRow[]
  specialCaseRows: OriginalHistoryRow[]
  countryRows: OriginalHistoryRow[]
}) {
  const countries = Array.from(new Set(
    countryRows
      .map(row => String(getRowField(row, ['страна', 'country', 'countries']) ?? '').trim())
      .filter(Boolean),
  )).sort((left, right) => left.localeCompare(right, 'ru'))

  return (
    <aside className="flex w-[740px] shrink-0 items-start gap-4" aria-label="Дополнительная статистика">
      <div className="w-1/2 shrink-0 space-y-4">
      <section className="rounded-xl border border-primary/30 bg-gradient-to-br from-card via-card to-primary/15 p-4">
        <header className="mb-3 border-b border-current/10 pb-3">
          <h2 className="text-base font-bold text-foreground">Студии у франчайзи</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">Распределение активных франчайзи по числу студий</p>
        </header>
        <div className="divide-y divide-current/10">
          {gradeRows.length === 0 ? (
            <div className="py-3 text-sm text-muted-foreground">Нет данных</div>
          ) : gradeRows.map(row => {
            const grade = getRowField(row, ['grade'])
            const partnersCount = getRowField(row, ['partners_count'])
            const partnersPct = getRowField(row, ['partners_pct'])

            return (
              <div key={String(grade)} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                <span className="text-foreground">{formatCellValue(grade)} {pluralizeRussian(Number(grade), 'студия', 'студии', 'студий')}</span>
                <span className="text-right tabular-nums">
                  <strong className="font-bold text-primary">{formatCellValue(partnersCount)}</strong>
                  <span className="ml-2 text-xs text-muted-foreground">{formatCellValue(partnersPct)}%</span>
                </span>
              </div>
            )
          })}
        </div>
      </section>

      <section className="rounded-xl border border-primary/30 bg-gradient-to-br from-card via-card to-primary/15 p-4">
        <header className="mb-3 border-b border-current/10 pb-3">
          <h2 className="text-base font-bold text-foreground">Страны присутствия</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">Страны, где есть открытые или сервисные студии</p>
        </header>
        {countries.length === 0 ? (
          <div className="text-sm text-muted-foreground">Нет данных</div>
        ) : (
          <div className="grid grid-cols-2 gap-x-6">
            {countries.map(country => (
              <span key={country} className="border-b border-current/10 py-2 text-sm text-foreground">
                {country}
              </span>
            ))}
          </div>
        )}
      </section>
      </div>

      <section className="w-1/2 shrink-0 rounded-xl border border-primary/30 bg-gradient-to-br from-card via-card to-primary/15 p-4">
        <header className="mb-3 border-b border-current/10 pb-3">
          <h2 className="text-base font-bold text-foreground">Особые случаи</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">Франчайзи и студии, требующие внимания</p>
        </header>
        <div className="space-y-2">
          {specialCaseRows.length === 0 ? (
            <div className="py-3 text-sm text-muted-foreground">Особых случаев нет</div>
          ) : specialCaseRows.map((row, index) => {
            const caseType = getRowField(row, ['тип', 'case'])
            const isStudioCase = /(?:^|\D)[34](?:\D|$)/.test(String(caseType ?? '').trim())

            return (
            <div key={`${String(getRowField(row, ['partner', 'партн']))}-${index}`} className="rounded-lg border border-border/70 bg-card/60 p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 text-sm font-semibold text-foreground">
                  {isStudioCase ? 'Студия' : 'Франчайзи'} №{formatCellValue(getRowField(row, ['partner', 'партн']))}
                </div>
                <div className="shrink-0 text-xs font-semibold text-primary">{formatCellValue(getRowField(row, ['город', 'city']))}</div>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">{formatCellValue(getRowField(row, ['активн', 'person']))}</div>
              <div className="mt-2 text-xs leading-4 text-foreground">{formatCellValue(caseType)}</div>
            </div>
            )
          })}
        </div>
      </section>
    </aside>
  )
}

function resolveColumnKey(row: OriginalHistoryRow, column: string) {
  const candidates = [column, ...(COLUMN_ALIASES[column] ?? []), ...(PARTNER_COLUMN_ALIASES[column] ?? [])]
  const keys = Object.keys(row)

  for (const candidate of candidates) {
    if (candidate in row) return candidate

    const target = normalizeColumnName(candidate)
    const matchingKey = keys.find(key => normalizeColumnName(key) === target)
    if (matchingKey) return matchingKey
  }

  return null
}

function getCellValue(row: OriginalHistoryRow, column: string) {
  const key = resolveColumnKey(row, column)
  return key ? row[key] : null
}

function isDateColumn(column: string) {
  return normalizeColumnName(column).includes('дата')
}

function normalizeDateFilterValue(value: unknown) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear()
    const month = String(value.getMonth() + 1).padStart(2, '0')
    const day = String(value.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const text = String(value ?? '').trim()
  const yearFirst = text.match(/^(\d{4})[-.](\d{2})[-.](\d{2})/)
  if (yearFirst) return `${yearFirst[1]}-${yearFirst[2]}-${yearFirst[3]}`

  const dayFirst = text.match(/^(\d{2})\.(\d{2})\.(\d{4})/)
  if (dayFirst) return `${dayFirst[3]}-${dayFirst[2]}-${dayFirst[1]}`

  return null
}

function isDateFilterActive(filter: DateFilterState | undefined) {
  if (!filter) return false
  if (filter.mode === 'before') return Boolean(filter.to)
  if (filter.mode === 'after') return Boolean(filter.from)
  return Boolean(filter.from && filter.to)
}

function matchesDateFilter(row: OriginalHistoryRow, column: string, filter: DateFilterState) {
  const value = normalizeDateFilterValue(getCellValue(row, column))
  if (!value) return false

  if (filter.mode === 'before') return value <= filter.to
  if (filter.mode === 'after') return value >= filter.from
  return value >= filter.from && value <= filter.to
}

function isChoiceFilterColumn(column: string) {
  const normalized = normalizeColumnName(column)
  return normalized.includes('статус') || normalized === 'тип подписки' || normalized === 'страна'
}

function getColumnWidthClass(column: string) {
  if (column === GLOBAL_STATISTICS_METRIC_COLUMN) return 'w-[320px] min-w-[320px] max-w-[320px]'

  return NARROW_COLUMNS.has(column) || isDateColumn(column)
    ? 'w-[112px] min-w-[112px] max-w-[112px]'
    : 'min-w-[170px] max-w-[300px]'
}

function formatDateValue(value: unknown) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear()
    const month = String(value.getMonth() + 1).padStart(2, '0')
    const day = String(value.getDate()).padStart(2, '0')
    return `${year}.${month}.${day}`
  }

  const text = String(value).trim()
  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (isoMatch) return `${isoMatch[1]}.${isoMatch[2]}.${isoMatch[3]}`

  return text
}

function translateCellValue(column: string, value: unknown) {
  if (typeof value !== 'string') return null

  const normalizedColumn = normalizeColumnName(column)
  const normalizedValue = value.trim().toUpperCase()

  if ((normalizedColumn === 'статус' || normalizedColumn.includes('статус студии')) && STATUS_MAP[normalizedValue]) {
    return STATUS_MAP[normalizedValue]
  }

  if (normalizedColumn.includes('статус владения') && OWNERSHIP_MAP[normalizedValue]) {
    return OWNERSHIP_MAP[normalizedValue]
  }

  if (
    (normalizedColumn.includes('subscription') || normalizedColumn.includes('подпис'))
    && SUBSCRIPTION_MAP[normalizedValue]
  ) {
    return SUBSCRIPTION_MAP[normalizedValue]
  }

  return null
}

function formatCellValue(value: unknown, column = '') {
  if (value === null || value === undefined || value === '') return '—'
  if (column && isDateColumn(column)) return formatDateValue(value)
  if (column) {
    const translated = translateCellValue(column, value)
    if (translated) return translated
  }
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toLocaleString('ru-RU')
  if (typeof value === 'boolean') return value ? 'Да' : 'Нет'
  if (typeof value === 'string') return value
  return JSON.stringify(value)
}

function getFilterText(row: OriginalHistoryRow, column: string) {
  const value = getCellValue(row, column)
  if (value === null || value === undefined) return ''
  return formatCellValue(value, column)
}

function getChoiceFilterValue(row: OriginalHistoryRow, column: string) {
  return formatCellValue(getCellValue(row, column), column)
}

function getComparableValue(row: OriginalHistoryRow, column: string) {
  const value = getCellValue(row, column)

  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'number') return value
  if (typeof value === 'boolean') return value ? 1 : 0

  const text = String(value).trim()
  const normalizedNumber = Number(text.replace(/\s/g, '').replace(',', '.'))
  if (!Number.isNaN(normalizedNumber) && text !== '') return normalizedNumber

  const timestamp = Date.parse(text)
  if (!Number.isNaN(timestamp) && /^\d{4}-\d{2}-\d{2}/.test(text)) return timestamp

  return formatCellValue(value, column).toLocaleLowerCase('ru')
}

function compareValues(left: string | number | null, right: string | number | null) {
  if (left === null && right === null) return 0
  if (left === null) return 1
  if (right === null) return -1

  if (typeof left === 'number' && typeof right === 'number') {
    return left - right
  }

  return String(left).localeCompare(String(right), 'ru', { numeric: true, sensitivity: 'base' })
}

function isNumericColumn(rows: OriginalHistoryRow[], column: string) {
  return rows.some(row => typeof getComparableValue(row, column) === 'number')
}

function getRowPartnerNumber(row: OriginalHistoryRow) {
  const value = getCellValue(row, 'partner_number') ?? getCellValue(row, ORIGINAL_COLUMN.partnerNumber)
  const numberValue = typeof value === 'number'
    ? value
    : Number(String(value ?? '').replace(/\s/g, '').trim())

  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : null
}

function toPositiveInteger(value: unknown) {
  const numberValue = typeof value === 'number'
    ? value
    : Number(String(value ?? '').replace(/\s/g, '').trim())

  return Number.isInteger(numberValue) && numberValue > 0 ? numberValue : null
}

function positiveIntegerFromInput(value: string) {
  return toPositiveInteger(value)
}

function attachmentRowsFromText(value: string): OriginalChangeCaseAttachment[] {
  return value
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => {
      try {
        const url = new URL(line)
        return { label: line, url: url.toString() }
      } catch {
        return { label: line }
      }
    })
}

function getRowStudioInternalId(row: OriginalHistoryRow) {
  return toPositiveInteger(
    getCellValue(row, 'studio_internal_id')
    ?? getCellValue(row, ORIGINAL_COLUMN.studioInternalId)
    ?? getCellValue(row, 'Внутр. ID (если нет №)')
  )
}

function getDateTimestamp(row: OriginalHistoryRow, column: string) {
  const value = getCellValue(row, column)
  if (value === null || value === undefined || value === '') return null
  const timestamp = Date.parse(String(value))
  return Number.isNaN(timestamp) ? null : timestamp
}

function getStudioStatusCode(row: OriginalHistoryRow) {
  const value = getCellValue(row, ORIGINAL_COLUMN.studioStatus)
  return String(value ?? '').trim().toUpperCase()
}

function hasDateValue(row: OriginalHistoryRow, column: string) {
  const value = getCellValue(row, column)
  return value !== null && value !== undefined && String(value).trim() !== ''
}

function isActiveOriginalRow(row: OriginalHistoryRow) {
  return ACTIVE_ORIGINAL_STATUSES.has(getStudioStatusCode(row))
}

function getOwnershipPhaseCode(row: OriginalHistoryRow) {
  const value = getCellValue(row, ORIGINAL_COLUMN.ownershipStatus)
  return String(value ?? '').trim().toUpperCase()
}

function isDiscoveryOwnershipRow(row: OriginalHistoryRow) {
  return DISCOVERY_OWNERSHIP_PHASES.has(getOwnershipPhaseCode(row))
}

function getCurrentOwnershipSortValue(row: OriginalHistoryRow) {
  return Math.max(
    getDateTimestamp(row, ORIGINAL_COLUMN.ownershipStartDate) ?? Number.NEGATIVE_INFINITY,
    getDateTimestamp(row, ORIGINAL_COLUMN.contributionDate) ?? Number.NEGATIVE_INFINITY,
    getDateTimestamp(row, ORIGINAL_COLUMN.openDate) ?? Number.NEGATIVE_INFINITY,
  )
}

function pickLatestStudioRows(rows: OriginalHistoryRow[], predicate: (row: OriginalHistoryRow) => boolean) {
  const latestByStudio = new Map<number, OriginalHistoryRow>()

  for (const row of rows) {
    const studioInternalId = getRowStudioInternalId(row)
    if (!studioInternalId || !predicate(row)) continue

    const current = latestByStudio.get(studioInternalId)
    if (!current || getCurrentOwnershipSortValue(row) >= getCurrentOwnershipSortValue(current)) {
      latestByStudio.set(studioInternalId, row)
    }
  }

  return Array.from(latestByStudio.values())
}

function filterOriginalRowsForMode(rows: OriginalHistoryRow[], filter: OriginalHistoryMode['rowFilter']) {
  if (!filter) return rows

  const activeRows = pickLatestStudioRows(rows, row =>
    isActiveOriginalRow(row) && !hasDateValue(row, ORIGINAL_COLUMN.ownershipEndDate)
  )

  if (filter === 'active') return activeRows

  const rowKeys = new Set(activeRows.map(getRowSelectionKey).filter((value): value is string => value !== null))
  const discoveryRows = [...activeRows]

  for (const row of rows) {
    if (!isDiscoveryOwnershipRow(row)) continue
    const key = getRowSelectionKey(row)
    if (!key || rowKeys.has(key)) continue

    rowKeys.add(key)
    discoveryRows.push(row)
  }

  return discoveryRows
}

function getRowSelectionKey(row: OriginalHistoryRow) {
  const studioInternalId = getRowStudioInternalId(row)
  if (!studioInternalId) return null

  const parts = [
    studioInternalId,
    formatCellValue(getCellValue(row, ORIGINAL_COLUMN.studioNumber), ORIGINAL_COLUMN.studioNumber),
    formatCellValue(getCellValue(row, ORIGINAL_COLUMN.partner), ORIGINAL_COLUMN.partner),
    formatCellValue(getCellValue(row, ORIGINAL_COLUMN.partnerNumber), ORIGINAL_COLUMN.partnerNumber),
    formatCellValue(getCellValue(row, ORIGINAL_COLUMN.ownershipStartDate), ORIGINAL_COLUMN.ownershipStartDate),
    formatCellValue(getCellValue(row, ORIGINAL_COLUMN.ownershipEndDate), ORIGINAL_COLUMN.ownershipEndDate),
    formatCellValue(getCellValue(row, ORIGINAL_COLUMN.studioStatus), ORIGINAL_COLUMN.studioStatus),
  ]

  return parts.map(part => String(part).trim()).join('|')
}

function getSelectedStudioFromRow(row: OriginalHistoryRow): OriginalSelectedStudio | null {
  const studioInternalId = getRowStudioInternalId(row)
  const selectionKey = getRowSelectionKey(row)
  if (!studioInternalId || !selectionKey) return null

  return {
    selectionKey,
    studioInternalId,
    studioNumber: formatCellValue(getCellValue(row, ORIGINAL_COLUMN.studioNumber), ORIGINAL_COLUMN.studioNumber),
    cityName: formatCellValue(getCellValue(row, ORIGINAL_COLUMN.city), ORIGINAL_COLUMN.city),
    status: formatCellValue(getCellValue(row, ORIGINAL_COLUMN.studioStatus), ORIGINAL_COLUMN.studioStatus),
    partnerName: formatCellValue(getCellValue(row, ORIGINAL_COLUMN.partner), ORIGINAL_COLUMN.partner),
    partnerNumber: getRowPartnerNumber(row),
  }
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  return 'Не удалось выполнить действие'
}

function mergePhotoPerson(
  current: Record<number, PartnerPhotoPerson[]>,
  person: PartnerPhotoPerson
) {
  const partnerNumber = Number(person.partner_number)
  const currentPeople = current[partnerNumber] ?? []
  const nextPeople = currentPeople.some(item => item.person_id === person.person_id)
    ? currentPeople.map(item => item.person_id === person.person_id ? person : item)
    : [...currentPeople, person]

  return {
    ...current,
    [partnerNumber]: nextPeople.sort((left, right) => left.person_id - right.person_id),
  }
}

function initialsForPerson(person: PartnerPhotoPerson) {
  const source = person.display_name || `${person.first_name} ${person.last_name}`.trim()
  const letters = source
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toLocaleUpperCase('ru'))
    .join('')

  return letters || String(person.person_id)
}

type PartnerPhotoCellProps = {
  partnerNumber: number | null
  people: PartnerPhotoPerson[]
  loading: boolean
  actionKey: string | null
  onUpload: (person: PartnerPhotoPerson, file: File) => void
  onDelete: (person: PartnerPhotoPerson) => void
  onLink: (person: PartnerPhotoPerson) => void
}

type OriginalCasesPanelProps = {
  selectedCategoryTitle: string
  onCategoryChange: (title: string) => void
  onAction: (action: OriginalCaseAction) => void
  onClose: () => void
}

function OriginalCasesPanel({
  selectedCategoryTitle,
  onCategoryChange,
  onAction,
  onClose,
}: OriginalCasesPanelProps) {
  const selectedCategory =
    ORIGINAL_CASE_CATEGORIES.find(category => category.title === selectedCategoryTitle)
    ?? ORIGINAL_CASE_CATEGORIES[0]

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onClose}>
    <section
      className="my-4 w-full max-w-5xl rounded-lg border border-border bg-card p-3 shadow-lg"
      onClick={event => event.stopPropagation()}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
        <div className="text-sm font-semibold text-foreground">Что произошло?</div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          Выберите категорию события, затем конкретный кейс. Готовые кейсы открывают мастер, остальные пока отмечены как следующие к реализации.
        </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          aria-label="Закрыть"
        >
          <X size={16} />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[260px_1fr]">
        <div className="space-y-1">
          {ORIGINAL_CASE_CATEGORIES.map(category => {
            const Icon = category.icon
            const isActive = category.title === selectedCategory.title

            return (
              <button
                key={category.title}
                type="button"
                onClick={() => onCategoryChange(category.title)}
                className={`flex w-full items-start gap-2 rounded-md border px-3 py-2 text-left transition-colors ${
                  isActive
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground'
                }`}
              >
                <Icon size={16} className="mt-0.5 shrink-0" />
                <span className="min-w-0">
                  <span className="block text-sm font-medium leading-5">{category.title}</span>
                  <span className={`block text-[11px] leading-4 ${isActive ? 'text-primary/80' : 'text-muted-foreground'}`}>
                    {category.description}
                  </span>
                </span>
              </button>
            )
          })}
        </div>

        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {selectedCategory.items.map(item => {
            const isReady = true
            const isTested = isOriginalCaseTested(item.label)

            return (
              <button
                key={item.label}
                type="button"
                disabled={!isReady}
                onClick={() => onAction(item.action)}
                className={`min-h-[86px] rounded-md border px-3 py-2 text-left transition-colors ${
                  isReady
                    ? 'border-border bg-background hover:border-primary/60 hover:bg-primary/5'
                    : 'cursor-not-allowed border-dashed border-border bg-muted/30 opacity-70'
                }`}
              >
                <span className="flex items-start justify-between gap-2">
                  <span className="text-sm font-semibold text-foreground">{item.label}</span>
                  <span className="flex shrink-0 items-center gap-1">
                    {isTested && (
                      <span className="rounded border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-emerald-700">
                        протестировано
                      </span>
                    )}
                    {!isReady && (
                      <span className="rounded border border-border bg-background px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                        позже
                      </span>
                    )}
                  </span>
                </span>
                <span className="mt-1 block text-xs leading-4 text-muted-foreground">{item.description}</span>
              </button>
            )
          })}
        </div>
      </div>
    </section>
    </div>
  )
}

type OriginalInformerDrawerProps = {
  studios: OriginalSelectedStudio[]
  onClose: () => void
  onCreated: () => void
}

type AddOnboardingCaseDrawerProps = {
  onClose: () => void
  onCreated: () => void
}

type AddOnboardingCaseType = 'new_franchisee_new_studio' | 'existing_partner_studio'

function AddOnboardingCaseDrawer({ onClose, onCreated }: AddOnboardingCaseDrawerProps) {
  const [caseType, setCaseType] = useState<AddOnboardingCaseType>('new_franchisee_new_studio')
  const [changedAt, setChangedAt] = useState(() => new Date().toISOString().slice(0, 10))
  const [details, setDetails] = useState('')
  const [comment, setComment] = useState('')
  const [cityName, setCityName] = useState('')
  const [country, setCountry] = useState('')
  const [ianaTimezone, setIanaTimezone] = useState('Europe/Moscow')
  const [cityMode, setCityMode] = useState<'existing' | 'new'>('existing')
  const [cityId, setCityId] = useState('')
  const [cityQuery, setCityQuery] = useState('')
  const [cities, setCities] = useState<OnboardingCity[]>([])
  const [populationThousands, setPopulationThousands] = useState('')
  const [cityPopulationGroup, setCityPopulationGroup] = useState('')
  const [cityLandGroup, setCityLandGroup] = useState('')
  const [populationGroups, setPopulationGroups] = useState<PopulationGroup[]>([])
  const [landGroups, setLandGroups] = useState<LandGroup[]>([])
  const [cityTimezones, setCityTimezones] = useState<string[]>(['Europe/Moscow'])
  const [partnerNumber, setPartnerNumber] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [gender, setGender] = useState<'м' | 'ж'>('ж')
  const [telegram, setTelegram] = useState('')
  const [secondPersonEnabled, setSecondPersonEnabled] = useState(false)
  const [secondFirstName, setSecondFirstName] = useState('')
  const [secondLastName, setSecondLastName] = useState('')
  const [secondGender, setSecondGender] = useState<'м' | 'ж'>('ж')
  const [secondTelegram, setSecondTelegram] = useState('')
  const [attachmentText, setAttachmentText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isNewFranchisee = caseType === 'new_franchisee_new_studio'
  const eventType: OriginalChangeCaseEventType = isNewFranchisee ? 'ONBOARDING_PARTNER_NEW_STUDIO' : 'ONBOARDING_STUDIO'
  const eventLabel = isNewFranchisee ? 'Новый франчайзи на новую студию' : 'Планирование студии франчайзи'
  const partnerNumberValue = positiveIntegerFromInput(partnerNumber)
  const populationThousandsValue = positiveIntegerFromInput(populationThousands)
  const cityPopulationGroupValue = positiveIntegerFromInput(cityPopulationGroup)
  const cityLandGroupValue = positiveIntegerFromInput(cityLandGroup)

  useEffect(() => {
    void Promise.all([fetchOnboardingCities(), fetchPopulationGroups(), fetchLandGroups(), fetchCityTimezones()])
      .then(([cityRows, populationRows, landRows, timezoneRows]) => {
        setCities(cityRows)
        setPopulationGroups(populationRows)
        setLandGroups(landRows)
        setCityTimezones(Array.from(new Set(['Europe/Moscow', ...timezoneRows])))
      })
      .catch(() => {
        setCities([])
        setPopulationGroups([])
        setLandGroups([])
      })
  }, [])

  useEffect(() => {
    if (populationThousands && !cityPopulationGroup) {
      const population = Number(populationThousands)
      const matchingGroup = populationGroups
        .filter(group => group.population_threshold <= population)
        .slice(-1)[0]
      if (matchingGroup) setCityPopulationGroup(String(matchingGroup.population_threshold))
    }
  }, [populationThousands, populationGroups, cityPopulationGroup])
  const filteredCities = cities
    .filter(row => `${row.city_name} ${row.country}`.toLowerCase().includes(cityQuery.trim().toLowerCase()))
    .slice(0, 60)
  const canSubmit = !submitting
    && Boolean(changedAt)
    && Boolean(details.trim())
    && (cityMode === 'existing'
      ? Boolean(cityId)
      : Boolean(cityName.trim())
        && Boolean(country.trim())
        && populationThousandsValue !== null
        && cityPopulationGroupValue !== null
        && cityLandGroupValue !== null)
    && (!isNewFranchisee || (firstName.trim() && lastName.trim()))
    && (!isNewFranchisee || !secondPersonEnabled || (secondFirstName.trim() && secondLastName.trim()))
    && (isNewFranchisee || partnerNumberValue !== null)

  const handleSubmit = async () => {
    if (!canSubmit) return

    setSubmitting(true)
    setError(null)

    try {
      await createOriginalChangeCase({
        eventType,
        changedAt,
        targetStatus: null,
        studioInternalIds: [],
        comment,
        attachments: attachmentRowsFromText(attachmentText),
        payload: {
          happened: details.trim(),
          event_label: eventLabel,
          city_id: cityMode === 'existing' ? Number(cityId) : null,
          city_name: cityMode === 'new' ? cityName.trim() : null,
          iana_timezone: cityMode === 'new' ? ianaTimezone.trim() : null,
          country: cityMode === 'new' ? country.trim() : null,
          population_thousands: cityMode === 'new' ? populationThousandsValue : null,
          city_population_group: cityMode === 'new' ? cityPopulationGroupValue : null,
          city_land_group: cityMode === 'new' ? cityLandGroupValue : null,
          partner_number: isNewFranchisee ? null : partnerNumberValue,
          new_franchisee_first_name: isNewFranchisee ? firstName.trim() : null,
          new_franchisee_last_name: isNewFranchisee ? lastName.trim() : null,
          new_franchisee_gender: isNewFranchisee ? gender : null,
          new_franchisee_tg_username: isNewFranchisee ? telegram.trim() || null : null,
          partner_group_persons: isNewFranchisee
            ? [
                {
                  first_name: firstName.trim(),
                  last_name: lastName.trim(),
                  gender,
                  tg_username: telegram.trim() || null,
                },
                ...(secondPersonEnabled ? [{
                  first_name: secondFirstName.trim(),
                  last_name: secondLastName.trim(),
                  gender: secondGender,
                  tg_username: secondTelegram.trim() || null,
                }] : []),
              ]
            : null,
        },
      })
      onCreated()
    } catch (submitError) {
      setError(getErrorMessage(submitError))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <aside
        className="h-full w-full max-w-xl overflow-y-auto border-l border-border bg-card shadow-xl"
        onClick={event => event.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-border bg-card px-4 py-3">
          <div className="min-w-0">
            <div className="text-base font-semibold text-foreground">Добавить кейс</div>
            <div className="mt-0.5 text-xs text-muted-foreground">Новая студия для действующего или нового франчайзи</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Закрыть"
            title="Закрыть"
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4 px-4 py-4">
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle size={17} className="mt-0.5 shrink-0" />
              <div>{error}</div>
            </div>
          )}

          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-foreground">Сценарий</span>
            <select
              value={caseType}
              onChange={event => setCaseType(event.target.value as AddOnboardingCaseType)}
              className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="new_franchisee_new_studio">Франчайзи на новую студию</option>
              <option value="existing_partner_studio">Планирование студии действующему франчайзи</option>
            </select>
          </label>

          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-foreground">Дата события</span>
            <input
              type="date"
              value={changedAt}
              onChange={event => setChangedAt(event.target.value)}
              className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
            />
          </label>

          {!isNewFranchisee && (
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-foreground">Номер действующего франчайзи</span>
              <input
                value={partnerNumber}
                onChange={event => setPartnerNumber(event.target.value)}
                inputMode="numeric"
                className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                placeholder="Например: 1234"
              />
            </label>
          )}

          {isNewFranchisee && (
            <section className="space-y-3 rounded-md border border-border bg-background p-3">
              <div className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">Новый франчайзи</div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block space-y-1.5">
                  <span className="text-sm font-medium text-foreground">Имя</span>
                  <input
                    value={firstName}
                    onChange={event => setFirstName(event.target.value)}
                    className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                  />
                </label>
                <label className="block space-y-1.5">
                  <span className="text-sm font-medium text-foreground">Фамилия</span>
                  <input
                    value={lastName}
                    onChange={event => setLastName(event.target.value)}
                    className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                  />
                </label>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block space-y-1.5">
                  <span className="text-sm font-medium text-foreground">Пол</span>
                  <select
                    value={gender}
                    onChange={event => setGender(event.target.value as 'м' | 'ж')}
                    className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="ж">ж</option>
                    <option value="м">м</option>
                  </select>
                </label>
                <label className="block space-y-1.5">
                  <span className="text-sm font-medium text-foreground">Telegram</span>
                  <input
                    value={telegram}
                    onChange={event => setTelegram(event.target.value)}
                    className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                    placeholder="без @, необязательно"
                  />
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={secondPersonEnabled}
                  onChange={event => setSecondPersonEnabled(event.target.checked)}
                  className="h-4 w-4 rounded border-border"
                />
                Добавить вторую персону
              </label>
              {secondPersonEnabled && (
                <div className="space-y-3 border-t border-border pt-3">
                  <div className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">Вторая персона</div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className="block space-y-1.5">
                      <span className="text-sm font-medium text-foreground">Имя</span>
                      <input
                        value={secondFirstName}
                        onChange={event => setSecondFirstName(event.target.value)}
                        className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                      />
                    </label>
                    <label className="block space-y-1.5">
                      <span className="text-sm font-medium text-foreground">Фамилия</span>
                      <input
                        value={secondLastName}
                        onChange={event => setSecondLastName(event.target.value)}
                        className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                      />
                    </label>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className="block space-y-1.5">
                      <span className="text-sm font-medium text-foreground">Пол</span>
                      <select
                        value={secondGender}
                        onChange={event => setSecondGender(event.target.value as 'м' | 'ж')}
                        className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                      >
                        <option value="ж">ж</option>
                        <option value="м">м</option>
                      </select>
                    </label>
                    <label className="block space-y-1.5">
                      <span className="text-sm font-medium text-foreground">Telegram</span>
                      <input
                        value={secondTelegram}
                        onChange={event => setSecondTelegram(event.target.value)}
                        className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                        placeholder="без @, необязательно"
                      />
                    </label>
                  </div>
                </div>
              )}
            </section>
          )}

          <section className="space-y-3 rounded-md border border-border bg-background p-3">
            <div className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">Новая студия</div>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={cityMode === 'new'}
                onChange={event => {
                  const isNew = event.target.checked
                  setCityMode(isNew ? 'new' : 'existing')
                  if (isNew) setCityId('')
                  else {
                    setCityName('')
                    setCountry('')
                    setPopulationThousands('')
                    setCityPopulationGroup('')
                    setCityLandGroup('')
                  }
                }}
                className="h-4 w-4 rounded border-border"
              />
              Новый город
            </label>
            {cityMode === 'existing' ? (
              <div className="space-y-1.5">
                <span className="text-sm font-medium text-foreground">Выберите город</span>
                <input
                  value={cityQuery}
                  onChange={event => setCityQuery(event.target.value)}
                  className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                  placeholder="Поиск по городу или стране"
                />
                {cityQuery.trim() && (
                  <select
                    value={cityId}
                    onChange={event => {
                      const value = event.target.value
                      const selected = cities.find(row => String(row.city_id) === value)
                      setCityId(value)
                      setCityName(selected?.city_name ?? '')
                      setCountry(selected?.country ?? '')
                    }}
                    size={5}
                    className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm outline-none focus:border-primary"
                  >
                    <option value="">— выберите город —</option>
                    {filteredCities.map(row => (
                      <option key={row.city_id} value={String(row.city_id)}>
                        {row.city_name} · {row.country}
                      </option>
                    ))}
                  </select>
                )}
                {cityId && !cityQuery.trim() && (
                  <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm text-foreground">
                    Выбран: {cityName} · {country}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="block space-y-1.5">
                    <span className="text-sm font-medium text-foreground">Название города</span>
                    <input value={cityName} onChange={event => setCityName(event.target.value)} className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary" placeholder="Город планируемой студии" />
                  </label>
                  <label className="block space-y-1.5">
                    <span className="text-sm font-medium text-foreground">Страна</span>
                    <input value={country} onChange={event => setCountry(event.target.value)} className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary" placeholder="Например: РФ" />
                  </label>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="block space-y-1.5">
                    <span className="text-sm font-medium text-foreground">Население, тыс. чел.</span>
                    <input type="number" min="1" step="1" value={populationThousands} onChange={event => {
                      const value = event.target.value
                      setPopulationThousands(value)
                      const matchingGroup = populationGroups.filter(group => group.population_threshold <= Number(value)).slice(-1)[0]
                      setCityPopulationGroup(matchingGroup ? String(matchingGroup.population_threshold) : '')
                    }} className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary" placeholder="Например: 31" />
                  </label>
                  <label className="block space-y-1.5">
                    <span className="text-sm font-medium text-foreground">Группа населения</span>
                    <select value={cityPopulationGroup} onChange={event => setCityPopulationGroup(event.target.value)} className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary">
                      <option value="">— выберите группу —</option>
                      {populationGroups.map(group => <option key={group.population_threshold} value={String(group.population_threshold)}>{group.population_threshold} — {group.description}</option>)}
                    </select>
                  </label>
                </div>
                <label className="block space-y-1.5">
                  <span className="text-sm font-medium text-foreground">Земельная группа</span>
                  <select value={cityLandGroup} onChange={event => setCityLandGroup(event.target.value)} className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary">
                    <option value="">— выберите группу —</option>
                    {landGroups.map(group => <option key={group.land_group_code} value={String(group.land_group_code)}>{group.land_group_code} — {group.land_group_name}</option>)}
                  </select>
                </label>
                <label className="block space-y-1.5">
                  <span className="text-sm font-medium text-foreground">Часовой пояс</span>
                  <select value={ianaTimezone} onChange={event => setIanaTimezone(event.target.value)} className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary">
                    {cityTimezones.map(timezone => <option key={timezone} value={timezone}>{timezone}</option>)}
                  </select>
                </label>
              </>
            )}
          </section>

          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-foreground">Описание события</span>
            <textarea
              value={details}
              onChange={event => setDetails(event.target.value)}
              rows={4}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary"
              placeholder="Что именно нужно проверить и провести"
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-foreground">Комментарий</span>
            <textarea
              value={comment}
              onChange={event => setComment(event.target.value)}
              rows={3}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary"
              placeholder="Дополнительный контекст для исполнителя"
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-foreground">Вложения</span>
            <textarea
              value={attachmentText}
              onChange={event => setAttachmentText(event.target.value)}
              rows={3}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary"
              placeholder="Ссылки или описание файлов, по одному на строку"
            />
          </label>

          <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
            <button
              type="button"
              onClick={onClose}
              className="h-9 rounded-md border border-border bg-card px-3 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="inline-flex h-9 items-center gap-2 rounded-md border border-primary bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting && <Loader2 size={15} className="animate-spin" />}
              Создать кейс
            </button>
          </div>
        </div>
      </aside>
    </div>
  )
}

function OriginalInformerDrawer({ studios, onClose, onCreated }: OriginalInformerDrawerProps) {
  const [eventType, setEventType] = useState<OriginalChangeCaseEventType>(
    TRANSFERABLE_INFORMER_EVENT_CONFIGS[0].type
  )
  const [changedAt, setChangedAt] = useState(() => new Date().toISOString().slice(0, 10))
  const [comment, setComment] = useState('')
  const [details, setDetails] = useState('')
  const [offerPrice, setOfferPrice] = useState('')
  const [offerCurrency, setOfferCurrency] = useState('RUB')
  const [offerBargainAllowed, setOfferBargainAllowed] = useState(false)
  const [newAddress, setNewAddress] = useState('')
  const [registerAddress, setRegisterAddress] = useState('')
  const [registerStudioNumber, setRegisterStudioNumber] = useState('')
  const [buyerPartnerNumber, setBuyerPartnerNumber] = useState('')
  const [sellerExitsNetwork, setSellerExitsNetwork] = useState(false)
  const [targetPartnerNumber, setTargetPartnerNumber] = useState(() => studios[0]?.partnerNumber ? String(studios[0].partnerNumber) : '')
  const [personFirstName, setPersonFirstName] = useState('')
  const [personLastName, setPersonLastName] = useState('')
  const [personGender, setPersonGender] = useState<'м' | 'ж'>('ж')
  const [personTelegram, setPersonTelegram] = useState('')
  const [newFranchiseeFirstName, setNewFranchiseeFirstName] = useState('')
  const [newFranchiseeLastName, setNewFranchiseeLastName] = useState('')
  const [newFranchiseeGender, setNewFranchiseeGender] = useState<'м' | 'ж'>('ж')
  const [newFranchiseeTelegram, setNewFranchiseeTelegram] = useState('')
  const [secondNewFranchiseeEnabled, setSecondNewFranchiseeEnabled] = useState(false)
  const [secondNewFranchiseeFirstName, setSecondNewFranchiseeFirstName] = useState('')
  const [secondNewFranchiseeLastName, setSecondNewFranchiseeLastName] = useState('')
  const [secondNewFranchiseeGender, setSecondNewFranchiseeGender] = useState<'м' | 'ж'>('ж')
  const [secondNewFranchiseeTelegram, setSecondNewFranchiseeTelegram] = useState('')
  const [personId, setPersonId] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [attachmentText, setAttachmentText] = useState('')
  const [bulkConfirmed, setBulkConfirmed] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isBulk = studios.length > 1
  const availableEventConfigs = useMemo(
    () => isBulk
      ? TRANSFERABLE_INFORMER_EVENT_CONFIGS.filter(item => item.supportsBulk)
      : TRANSFERABLE_INFORMER_EVENT_CONFIGS,
    [isBulk]
  )
  const eventConfig = TRANSFERABLE_INFORMER_EVENT_CONFIGS.find(item => item.type === eventType)
    ?? TRANSFERABLE_INFORMER_EVENT_CONFIGS[0]
  const bulkBlocked = isBulk && !eventConfig.supportsBulk
  const needsOfferFields = eventType === 'STATE_CHANGE_SALE_OFFER' || eventType === 'STATE_CHANGE_UPDATE_SALE_OFFER'
  const needsAddress = eventType === 'STATE_CHANGE_CHANGE_ADDRESS'
  const needsRegistrationFields = eventType === 'ONBOARDING_REGISTER'
  const needsBuyer = eventType === 'PURCHASE_PLAN' || eventType === 'PURCHASE_COMPLETE'
  const needsNewFranchisee = eventType === 'ONBOARDING_PARTNER_BUY_STUDIO'
  const needsPartnerNumber = eventType.startsWith('PARTNER_STATUS_') || eventType.startsWith('PARTNER_GROUP_')
  const needsPersonName = eventType === 'PARTNER_GROUP_ADD_PERSON' || eventType === 'PARTNER_GROUP_END_PERSON'
  const needsPersonId = eventType === 'PARTNER_GROUP_DISPLAY_NAME'
  const offerPriceNumber = Number(offerPrice.replace(/\s/g, '').replace(',', '.'))
  const buyerPartnerNumberValue = positiveIntegerFromInput(buyerPartnerNumber)
  const targetPartnerNumberValue = positiveIntegerFromInput(targetPartnerNumber)
  const personIdValue = positiveIntegerFromInput(personId)
  const canSubmit = !submitting
    && !bulkBlocked
    && changedAt
    && (!isBulk || bulkConfirmed)
    && (!needsOfferFields || (Number.isFinite(offerPriceNumber) && offerPriceNumber > 0))
    && (!needsAddress || newAddress.trim())
    && (!needsRegistrationFields || registerAddress.trim())
    && (!needsBuyer || buyerPartnerNumberValue !== null)
    && (!needsNewFranchisee || (newFranchiseeFirstName.trim() && newFranchiseeLastName.trim()))
    && (!needsNewFranchisee || !secondNewFranchiseeEnabled || (secondNewFranchiseeFirstName.trim() && secondNewFranchiseeLastName.trim()))
    && (!needsPartnerNumber || targetPartnerNumberValue !== null)
    && (!needsPersonName || (personFirstName.trim() && personLastName.trim()))
    && (!needsPersonId || personIdValue !== null)
  const targetStatus = eventConfig.targetStatus

  useEffect(() => {
    if (availableEventConfigs.some(item => item.type === eventType)) return
    setEventType(availableEventConfigs[0]?.type ?? TRANSFERABLE_INFORMER_EVENT_CONFIGS[0].type)
    setBulkConfirmed(false)
  }, [availableEventConfigs, eventType])

  useEffect(() => {
    setBulkConfirmed(false)
    if (studios[0]?.partnerNumber) setTargetPartnerNumber(String(studios[0].partnerNumber))
  }, [eventType, studios])

  const handleSubmit = async () => {
    if (!canSubmit) return

    setSubmitting(true)
    setError(null)

    const attachments = attachmentRowsFromText(attachmentText)

    try {
      await createOriginalChangeCase({
        eventType,
        changedAt,
        targetStatus,
        studioInternalIds: Array.from(new Set(studios.map(studio => studio.studioInternalId))),
        comment,
        attachments,
        payload: {
          happened: details.trim(),
          event_label: eventConfig.label,
          target_status: targetStatus,
          closure_type: null,
          offer_price: needsOfferFields ? offerPriceNumber : null,
          offer_currency: needsOfferFields ? offerCurrency : null,
          offer_is_bargain_allowed: needsOfferFields ? offerBargainAllowed : null,
          new_address: needsAddress ? newAddress.trim() : null,
          register_address: needsRegistrationFields ? registerAddress.trim() : null,
          register_studio_number: needsRegistrationFields ? positiveIntegerFromInput(registerStudioNumber) : null,
          buyer_partner_number: needsBuyer ? buyerPartnerNumberValue : null,
          seller_exits_network: eventType === 'PURCHASE_COMPLETE' ? sellerExitsNetwork : null,
          new_franchisee_first_name: needsNewFranchisee ? newFranchiseeFirstName.trim() : null,
          new_franchisee_last_name: needsNewFranchisee ? newFranchiseeLastName.trim() : null,
          new_franchisee_gender: needsNewFranchisee ? newFranchiseeGender : null,
          new_franchisee_tg_username: needsNewFranchisee ? newFranchiseeTelegram.trim() || null : null,
          partner_group_persons: needsNewFranchisee
            ? [
                {
                  first_name: newFranchiseeFirstName.trim(),
                  last_name: newFranchiseeLastName.trim(),
                  gender: newFranchiseeGender,
                  tg_username: newFranchiseeTelegram.trim() || null,
                },
                ...(secondNewFranchiseeEnabled ? [{
                  first_name: secondNewFranchiseeFirstName.trim(),
                  last_name: secondNewFranchiseeLastName.trim(),
                  gender: secondNewFranchiseeGender,
                  tg_username: secondNewFranchiseeTelegram.trim() || null,
                }] : []),
              ]
            : null,
          partner_number: needsPartnerNumber ? targetPartnerNumberValue : null,
          person_first_name: needsPersonName ? personFirstName.trim() : null,
          person_last_name: needsPersonName ? personLastName.trim() : null,
          person_gender: eventType === 'PARTNER_GROUP_ADD_PERSON' ? personGender : null,
          person_tg_username: eventType === 'PARTNER_GROUP_ADD_PERSON' ? personTelegram.trim() || null : null,
          person_id: needsPersonId ? personIdValue : null,
          display_name: eventType === 'PARTNER_GROUP_DISPLAY_NAME' ? displayName.trim() : null,
          selected_studios: studios,
        },
      })
      onCreated()
    } catch (submitError) {
      setError(getErrorMessage(submitError))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <aside
        className="h-full w-full max-w-xl overflow-y-auto border-l border-border bg-card shadow-xl"
        onClick={event => event.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-border bg-card px-4 py-3">
          <div className="min-w-0">
            <div className="text-base font-semibold text-foreground">Передать информацию</div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              Выбрано студий: {studios.length.toLocaleString('ru-RU')}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Закрыть"
            title="Закрыть"
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4 px-4 py-4">
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle size={17} className="mt-0.5 shrink-0" />
              <div>{error}</div>
            </div>
          )}

          <section className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">Студии</div>
            <div className="max-h-40 overflow-y-auto rounded-md border border-border">
              {studios.map(studio => (
                <div key={studio.selectionKey} className="border-b border-border px-3 py-2 text-sm last:border-b-0">
                  <div className="font-medium text-foreground">
                    № {studio.studioNumber} · {studio.cityName}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {studio.partnerName} · текущий статус: {studio.status}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-foreground">Что произошло?</span>
            <select
              value={eventType}
              onChange={event => {
                setEventType(event.target.value as OriginalChangeCaseEventType)
                setBulkConfirmed(false)
              }}
              className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
            >
              {availableEventConfigs.map(item => (
                <option key={item.type} value={item.type}>{item.label}</option>
              ))}
            </select>
            <span className="block text-xs text-muted-foreground">{eventConfig.description}</span>
          </label>

          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertCircle size={17} className="mt-0.5 shrink-0" aria-hidden="true" />
            <div>
              Статусы «Без продаж» и «Закрыта» можно передать только через{' '}
              <Link
                to="/management/studio-closures"
                onClick={onClose}
                className="font-semibold underline underline-offset-2 hover:text-amber-950"
              >
                экран закрытия студий
              </Link>
              .
            </div>
          </div>

          {bulkBlocked && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              Этот тип события нельзя создать массово. Выберите одну студию или создайте отдельные кейсы.
            </div>
          )}

          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-foreground">Дата изменения</span>
            <input
              type="date"
              value={changedAt}
              onChange={event => {
                setChangedAt(event.target.value)
                setBulkConfirmed(false)
              }}
              className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
            />
          </label>

          {needsOfferFields && (
            <section className="space-y-3 rounded-md border border-border bg-background p-3">
              <div className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">Условия оффера</div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_120px]">
                <label className="block space-y-1.5">
                  <span className="text-sm font-medium text-foreground">Цена</span>
                  <input
                    value={offerPrice}
                    onChange={event => setOfferPrice(event.target.value)}
                    inputMode="decimal"
                    className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                    placeholder="1250000"
                  />
                </label>
                <label className="block space-y-1.5">
                  <span className="text-sm font-medium text-foreground">Валюта</span>
                  <input
                    value={offerCurrency}
                    onChange={event => setOfferCurrency(event.target.value.toUpperCase())}
                    className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                    placeholder="RUB"
                  />
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={offerBargainAllowed}
                  onChange={event => setOfferBargainAllowed(event.target.checked)}
                  className="h-4 w-4 rounded border-border"
                />
                Торг разрешен
              </label>
            </section>
          )}

          {needsAddress && (
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-foreground">Новый адрес</span>
              <textarea
                value={newAddress}
                onChange={event => setNewAddress(event.target.value)}
                rows={3}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary"
                placeholder="Адрес, который нужно внести в базу"
              />
            </label>
          )}

          {needsRegistrationFields && (
            <section className="space-y-3 rounded-md border border-border bg-background p-3">
              <label className="block space-y-1.5">
                <span className="text-sm font-medium text-foreground">Адрес студии</span>
                <textarea
                  value={registerAddress}
                  onChange={event => setRegisterAddress(event.target.value)}
                  rows={3}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary"
                  placeholder="Адрес для регистрации студии"
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-sm font-medium text-foreground">Номер студии (если известен)</span>
                <input
                  value={registerStudioNumber}
                  onChange={event => setRegisterStudioNumber(event.target.value)}
                  inputMode="numeric"
                  className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                  placeholder="Можно оставить пустым"
                />
              </label>
            </section>
          )}

          {needsBuyer && (
            <section className="space-y-3 rounded-md border border-border bg-background p-3">
              <div className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">Покупка</div>
              <label className="block space-y-1.5">
                <span className="text-sm font-medium text-foreground">Номер франчайзи-покупателя</span>
                <input
                  value={buyerPartnerNumber}
                  onChange={event => setBuyerPartnerNumber(event.target.value)}
                  inputMode="numeric"
                  className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                  placeholder="Например: 1234"
                />
              </label>
              {eventType === 'PURCHASE_COMPLETE' && (
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={sellerExitsNetwork}
                    onChange={event => setSellerExitsNetwork(event.target.checked)}
                    className="h-4 w-4 rounded border-border"
                  />
                  Продавец уходит из сети
                </label>
              )}
            </section>
          )}

          {needsNewFranchisee && (
            <section className="space-y-3 rounded-md border border-border bg-background p-3">
              <div className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">Новый франчайзи</div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block space-y-1.5">
                  <span className="text-sm font-medium text-foreground">Имя</span>
                  <input
                    value={newFranchiseeFirstName}
                    onChange={event => setNewFranchiseeFirstName(event.target.value)}
                    className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                  />
                </label>
                <label className="block space-y-1.5">
                  <span className="text-sm font-medium text-foreground">Фамилия</span>
                  <input
                    value={newFranchiseeLastName}
                    onChange={event => setNewFranchiseeLastName(event.target.value)}
                    className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                  />
                </label>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block space-y-1.5">
                  <span className="text-sm font-medium text-foreground">Пол</span>
                  <select
                    value={newFranchiseeGender}
                    onChange={event => setNewFranchiseeGender(event.target.value as 'м' | 'ж')}
                    className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="ж">ж</option>
                    <option value="м">м</option>
                  </select>
                </label>
                <label className="block space-y-1.5">
                  <span className="text-sm font-medium text-foreground">Telegram</span>
                  <input
                    value={newFranchiseeTelegram}
                    onChange={event => setNewFranchiseeTelegram(event.target.value)}
                    className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                    placeholder="без @, необязательно"
                  />
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={secondNewFranchiseeEnabled}
                  onChange={event => setSecondNewFranchiseeEnabled(event.target.checked)}
                  className="h-4 w-4 rounded border-border"
                />
                Добавить вторую персону
              </label>
              {secondNewFranchiseeEnabled && (
                <div className="space-y-3 border-t border-border pt-3">
                  <div className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">Вторая персона</div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className="block space-y-1.5">
                      <span className="text-sm font-medium text-foreground">Имя</span>
                      <input
                        value={secondNewFranchiseeFirstName}
                        onChange={event => setSecondNewFranchiseeFirstName(event.target.value)}
                        className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                      />
                    </label>
                    <label className="block space-y-1.5">
                      <span className="text-sm font-medium text-foreground">Фамилия</span>
                      <input
                        value={secondNewFranchiseeLastName}
                        onChange={event => setSecondNewFranchiseeLastName(event.target.value)}
                        className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                      />
                    </label>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className="block space-y-1.5">
                      <span className="text-sm font-medium text-foreground">Пол</span>
                      <select
                        value={secondNewFranchiseeGender}
                        onChange={event => setSecondNewFranchiseeGender(event.target.value as 'м' | 'ж')}
                        className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                      >
                        <option value="ж">ж</option>
                        <option value="м">м</option>
                      </select>
                    </label>
                    <label className="block space-y-1.5">
                      <span className="text-sm font-medium text-foreground">Telegram</span>
                      <input
                        value={secondNewFranchiseeTelegram}
                        onChange={event => setSecondNewFranchiseeTelegram(event.target.value)}
                        className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                        placeholder="без @, необязательно"
                      />
                    </label>
                  </div>
                </div>
              )}
            </section>
          )}

          {needsPartnerNumber && (
            <section className="space-y-3 rounded-md border border-border bg-background p-3">
              <div className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">Франчайзи</div>
              <label className="block space-y-1.5">
                <span className="text-sm font-medium text-foreground">Номер франчайзи</span>
                <input
                  value={targetPartnerNumber}
                  onChange={event => setTargetPartnerNumber(event.target.value)}
                  inputMode="numeric"
                  className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                  placeholder="Номер франчайзи"
                />
              </label>
              {needsPersonName && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="block space-y-1.5">
                    <span className="text-sm font-medium text-foreground">Имя</span>
                    <input
                      value={personFirstName}
                      onChange={event => setPersonFirstName(event.target.value)}
                      className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                    />
                  </label>
                  <label className="block space-y-1.5">
                    <span className="text-sm font-medium text-foreground">Фамилия</span>
                    <input
                      value={personLastName}
                      onChange={event => setPersonLastName(event.target.value)}
                      className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                    />
                  </label>
                </div>
              )}
              {eventType === 'PARTNER_GROUP_ADD_PERSON' && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="block space-y-1.5">
                    <span className="text-sm font-medium text-foreground">Пол</span>
                    <select
                      value={personGender}
                      onChange={event => setPersonGender(event.target.value as 'м' | 'ж')}
                      className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                    >
                      <option value="ж">ж</option>
                      <option value="м">м</option>
                    </select>
                  </label>
                  <label className="block space-y-1.5">
                    <span className="text-sm font-medium text-foreground">Telegram</span>
                    <input
                      value={personTelegram}
                      onChange={event => setPersonTelegram(event.target.value)}
                      className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                      placeholder="без @, необязательно"
                    />
                  </label>
                </div>
              )}
              {eventType === 'PARTNER_GROUP_DISPLAY_NAME' && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="block space-y-1.5">
                    <span className="text-sm font-medium text-foreground">ID персоны</span>
                    <input
                      value={personId}
                      onChange={event => setPersonId(event.target.value)}
                      inputMode="numeric"
                      className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                    />
                  </label>
                  <label className="block space-y-1.5">
                    <span className="text-sm font-medium text-foreground">Форма имени</span>
                    <input
                      value={displayName}
                      onChange={event => setDisplayName(event.target.value)}
                      className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                      placeholder="Пусто — паспортное ФИО"
                    />
                  </label>
                </div>
              )}
            </section>
          )}

          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-foreground">Описание события</span>
            <textarea
              value={details}
              onChange={event => setDetails(event.target.value)}
              rows={4}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary"
              placeholder="Что именно нужно проверить и провести"
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-foreground">Комментарий</span>
            <textarea
              value={comment}
              onChange={event => setComment(event.target.value)}
              rows={3}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary"
              placeholder="Дополнительный контекст для исполнителя"
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-foreground">Вложения</span>
            <textarea
              value={attachmentText}
              onChange={event => setAttachmentText(event.target.value)}
              rows={3}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary"
              placeholder="Ссылки или описание файлов, по одному на строку"
            />
          </label>

          {isBulk && eventConfig.supportsBulk && (
            <div className="space-y-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <div>
                Вы выбрали {studios.length.toLocaleString('ru-RU')} студий. Указанное изменение и дата будут применены ко всем выбранным студиям. Проверьте данные перед отправкой.
              </div>
              <div className="grid gap-1 text-xs">
                <div>Тип события: {eventConfig.label}</div>
                <div>Целевой статус: {targetStatus || 'не задан'}</div>
                <div>Дата изменения: {changedAt || 'не задана'}</div>
              </div>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={bulkConfirmed}
                  onChange={event => setBulkConfirmed(event.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-border"
                />
                <span>Подтверждаю массовое создание кейса с одной датой и одним целевым состоянием.</span>
              </label>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
            <button
              type="button"
              onClick={onClose}
              className="h-9 rounded-md border border-border bg-card px-3 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="inline-flex h-9 items-center gap-2 rounded-md border border-primary bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting && <Loader2 size={15} className="animate-spin" />}
              Создать кейс
            </button>
          </div>
        </div>
      </aside>
    </div>
  )
}

function PartnerPhotoCell({
  partnerNumber,
  people,
  loading,
  actionKey,
  onUpload,
  onDelete,
  onLink,
}: PartnerPhotoCellProps) {
  if (!partnerNumber) {
    return <span className="text-muted-foreground">—</span>
  }

  if (loading) {
    return (
      <div className="flex h-10 items-center gap-2 text-xs text-muted-foreground">
        <Loader2 size={14} className="animate-spin" />
        Загрузка
      </div>
    )
  }

  if (people.length === 0) {
    return <span className="text-xs text-muted-foreground">Нет активных персон</span>
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {people.map(person => {
        const busy = actionKey === `${person.partner_number}:${person.person_id}`
        const inputId = `partner-photo-${person.partner_number}-${person.person_id}`
        const displayName = person.display_name || `${person.first_name} ${person.last_name}`.trim() || `Персона ${person.person_id}`
        const effectivePath = person.photo_path ?? person.inferred_storage_path
        const hasUnlinkedPhoto = !person.photo_path && Boolean(person.inferred_storage_path)

        return (
          <div key={person.person_id} className="w-11" title={`${displayName} #${person.person_id}${effectivePath ? ` · ${effectivePath}` : ''}`}>
            <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-md border border-border bg-muted text-[10px] font-semibold text-muted-foreground">
              {person.photo_url ? (
                <img
                  src={person.photo_url}
                  alt={displayName}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              ) : (
                <span>{initialsForPerson(person)}</span>
              )}
            </div>

            <input
              id={inputId}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              disabled={busy}
              onChange={event => {
                const file = event.target.files?.[0]
                event.currentTarget.value = ''
                if (file) onUpload(person, file)
              }}
            />

            <div className="mt-1 grid grid-cols-2 gap-1">
              {hasUnlinkedPhoto && (
              <button
                type="button"
                onClick={() => onLink(person)}
                disabled={busy}
                className="col-span-2 inline-flex h-5 items-center justify-center rounded border border-border bg-card text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50"
                title="Привязать найденный файл"
                aria-label={`Привязать фото ${displayName}`}
              >
                {busy ? <Loader2 size={11} className="animate-spin" /> : <LinkIcon size={11} />}
              </button>
              )}

              <label
                htmlFor={inputId}
                className={`inline-flex h-5 items-center justify-center rounded border border-border bg-card text-muted-foreground transition-colors hover:bg-accent hover:text-foreground ${busy ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}
                title={effectivePath ? 'Заменить фото' : 'Загрузить фото'}
                aria-label={`${effectivePath ? 'Заменить' : 'Загрузить'} фото ${displayName}`}
              >
                {busy ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
              </label>

              <button
                type="button"
                onClick={() => onDelete(person)}
                disabled={busy || !effectivePath}
                className="inline-flex h-5 items-center justify-center rounded border border-border bg-card text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-700 disabled:opacity-40"
                title="Удалить фото"
                aria-label={`Удалить фото ${displayName}`}
              >
                {busy ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
              </button>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export function OriginalHistoryPage() {
  const tableAreaRef = useRef<HTMLDivElement | null>(null)
  const { isSuperAdmin } = useSuperAdmin()
  const [activeModeId, setActiveModeId] = useState<OriginalHistoryModeId>('discovery')
  const [rows, setRows] = useState<OriginalHistoryRow[]>([])
  const [gradeStatsRows, setGradeStatsRows] = useState<OriginalHistoryRow[]>([])
  const [specialCaseRows, setSpecialCaseRows] = useState<OriginalHistoryRow[]>([])
  const [countryRows, setCountryRows] = useState<OriginalHistoryRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sort, setSort] = useState<SortState | null>(null)
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<Record<string, string>>({})
  const [choiceFilters, setChoiceFilters] = useState<Record<string, string[]>>({})
  const [dateFilters, setDateFilters] = useState<Record<string, DateFilterState>>({})
  const [openColumnMenu, setOpenColumnMenu] = useState<string | null>(null)
  const [columnMenuPosition, setColumnMenuPosition] = useState<ColumnMenuPosition | null>(null)
  const [photoPeopleByPartner, setPhotoPeopleByPartner] = useState<Record<number, PartnerPhotoPerson[]>>({})
  const [photoLoading, setPhotoLoading] = useState(false)
  const [photoError, setPhotoError] = useState<string | null>(null)
  const [photoActionKey, setPhotoActionKey] = useState<string | null>(null)
  const [purchaseMode, setPurchaseMode] = useState<StudioPurchaseMode | null>(null)
  const [onboardingMode, setOnboardingMode] = useState<StudioOnboardingMode | null>(null)
  const [stateChangeMode, setStateChangeMode] = useState<StudioStateChangeMode | null>(null)
  const [partnerStatusMode, setPartnerStatusMode] = useState<PartnerStatusMode | null>(null)
  const [partnerGroupMode, setPartnerGroupMode] = useState<PartnerGroupMembershipMode | null>(null)
  const [subscriptionModelOpen, setSubscriptionModelOpen] = useState(false)
  const [onboardingTarget, setOnboardingTarget] = useState<'new_studio' | 'buy_studio'>('new_studio')
  const [selectedCaseCategory, setSelectedCaseCategory] = useState(ORIGINAL_CASE_CATEGORIES[0].title)
  const [casePickerOpen, setCasePickerOpen] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  const [informerDrawerOpen, setInformerDrawerOpen] = useState(false)
  const [addOnboardingDrawerOpen, setAddOnboardingDrawerOpen] = useState(false)
  const [informerSuccess, setInformerSuccess] = useState<string | null>(null)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const canEditOriginalCases = isSuperAdmin === true

  const activeMode = useMemo(() => {
    return ORIGINAL_HISTORY_MODES.find(mode => mode.id === activeModeId) ?? ORIGINAL_HISTORY_MODES[0]
  }, [activeModeId])

  const modeRows = useMemo(() => {
    return filterOriginalRowsForMode(rows, activeMode.rowFilter)
  }, [activeMode.rowFilter, rows])

  const columns = useMemo(() => {
    if (activeMode.columns) {
      if (activeModeId === 'partners') return activeMode.columns
      if (modeRows.length === 0) return activeMode.columns
      return activeMode.columns.filter(column => modeRows.some(row => resolveColumnKey(row, column) !== null))
    }

    const dynamicColumns = Array.from(new Set(modeRows.flatMap(row => Object.keys(row))))
    return activeMode.excludeColumn ? dynamicColumns.filter(column => !activeMode.excludeColumn?.(column)) : dynamicColumns
  }, [activeMode.columns, activeMode.excludeColumn, activeModeId, modeRows])

  const activeFilters = useMemo(() => {
    return Object.entries(filters)
      .map(([column, value]) => [column, value.trim()] as const)
      .filter(([, value]) => value.length > 0)
  }, [filters])

  const activeChoiceFilters = useMemo(() => {
    return Object.entries(choiceFilters)
      .map(([column, values]) => [column, values.filter(value => value.trim().length > 0)] as const)
      .filter(([, values]) => values.length > 0)
  }, [choiceFilters])

  const activeDateFilters = useMemo(() => {
    return Object.entries(dateFilters).filter(([, filter]) => isDateFilterActive(filter))
  }, [dateFilters])

  const normalizedSearch = search.trim().toLocaleLowerCase('ru')
  const searchColumns = activeModeId === 'statistics' ? [] : ORIGINAL_SEARCH_COLUMNS[activeModeId]
  const searchPlaceholder = activeModeId === 'statistics' ? '' : ORIGINAL_SEARCH_PLACEHOLDERS[activeModeId]
  const filterCount = activeFilters.length + activeChoiceFilters.length + activeDateFilters.length + Number(normalizedSearch.length > 0)

  const choiceFilterOptions = useMemo(() => {
    const options: Record<string, string[]> = {}

    for (const column of columns) {
      if (!isChoiceFilterColumn(column)) continue

      const values = new Set<string>()
      for (const row of modeRows) {
        values.add(getChoiceFilterValue(row, column))
      }

      options[column] = Array.from(values).sort((left, right) =>
        left.localeCompare(right, 'ru', { numeric: true, sensitivity: 'base' })
      )
    }

    return options
  }, [columns, modeRows])

  const filteredRows = useMemo(() => {
    if (activeFilters.length === 0 && activeChoiceFilters.length === 0 && activeDateFilters.length === 0 && !normalizedSearch) return modeRows

    return modeRows.filter(row =>
      (!normalizedSearch || searchColumns.some(column =>
        getFilterText(row, column).toLocaleLowerCase('ru').includes(normalizedSearch)
      ))
      && activeFilters.every(([column, filterValue]) => {
        const cellText = getFilterText(row, column).toLocaleLowerCase('ru')
        return cellText.includes(filterValue.toLocaleLowerCase('ru'))
      })
      && activeChoiceFilters.every(([column, selectedValues]) =>
        selectedValues.includes(getChoiceFilterValue(row, column))
      )
      && activeDateFilters.every(([column, filter]) => matchesDateFilter(row, column, filter))
    )
  }, [activeChoiceFilters, activeDateFilters, activeFilters, modeRows, normalizedSearch, searchColumns])

  const sortedRows = useMemo(() => {
    if (!sort) return filteredRows

    return [...filteredRows].sort((leftRow, rightRow) => {
      const result = compareValues(
        getComparableValue(leftRow, sort.column),
        getComparableValue(rightRow, sort.column)
      )
      return sort.direction === 'asc' ? result : -result
    })
  }, [filteredRows, sort])

  const numericColumns = useMemo(() => {
    return new Set(columns.filter(column => isNumericColumn(modeRows, column)))
  }, [columns, modeRows])

  const isStatisticsMode = activeModeId === 'statistics'
  const showPartnerPhotos = activeModeId === 'partners'
  const showStudioSelection = activeModeId === 'history' || activeModeId === 'discovery'
  const ActiveModeIcon = activeMode.icon

  const selectedStudios = useMemo(() => {
    const selected = new Set(selectedRowKeys)
    return modeRows
      .map(getSelectedStudioFromRow)
      .filter((studio): studio is OriginalSelectedStudio => studio !== null && selected.has(studio.selectionKey))
  }, [modeRows, selectedRowKeys])

  const visibleRowKeys = useMemo(() => {
    return sortedRows
      .map(getRowSelectionKey)
      .filter((value): value is string => value !== null)
  }, [sortedRows])

  const allVisibleSelected = visibleRowKeys.length > 0 && visibleRowKeys.every(key => selectedRowKeys.includes(key))

  const loadRows = useCallback(async () => {
    setLoading(true)
    setError(null)
    setGradeStatsRows([])
    setSpecialCaseRows([])
    setCountryRows([])

    const reporting = (supabase as unknown as ReportingRpcClient).schema(activeMode.schema ?? 'reporting')
    const results = isStatisticsMode
      ? await Promise.all([
          reporting.rpc(activeMode.rpc),
          reporting.rpc('partner_studio_grade_stats'),
          reporting.rpc('get_partners_special_cases_new1'),
          reporting.rpc('original_history_int1'),
        ])
      : [await reporting.rpc(activeMode.rpc)]

    const [{ data, error: rpcError }, gradeResult, specialCaseResult, countryResult] = results

    if (rpcError) {
      setError(rpcError.message || 'Не удалось загрузить исходные данные')
      setRows([])
    } else {
      const nextRows = toRows(data)
      setRows(isStatisticsMode ? normalizeGlobalStatisticsRows(nextRows) : nextRows)
      if (isStatisticsMode && gradeResult && specialCaseResult && countryResult) {
        if (gradeResult.error || specialCaseResult.error || countryResult.error) {
          setError(gradeResult.error?.message || specialCaseResult.error?.message || countryResult.error?.message || 'Не удалось загрузить дополнительную статистику')
          setRows([])
        } else {
          setGradeStatsRows(toRows(gradeResult.data))
          setSpecialCaseRows(toRows(specialCaseResult.data))
          setCountryRows(pickLatestStudioRows(toRows(countryResult.data), row =>
            COUNTRY_PRESENCE_STATUSES.has(getStudioStatusCode(row))
              && !hasDateValue(row, ORIGINAL_COLUMN.ownershipEndDate),
          ))
        }
      }
    }

    setLoading(false)
  }, [activeMode.rpc, activeMode.schema, isStatisticsMode])

  const setColumnSort = (column: string, direction: SortDirection) => {
    setSort({ column, direction })
    setOpenColumnMenu(null)
  }

  const toggleColumnMenu = (column: string, trigger: HTMLButtonElement, alignLeft: boolean) => {
    if (openColumnMenu === column) {
      setOpenColumnMenu(null)
      setColumnMenuPosition(null)
      return
    }

    const rect = trigger.getBoundingClientRect()
    const menuWidth = 256
    const viewportPadding = 8
    const preferredLeft = alignLeft ? rect.left : rect.right - menuWidth
    const left = Math.min(
      Math.max(preferredLeft, viewportPadding),
      Math.max(viewportPadding, window.innerWidth - menuWidth - viewportPadding),
    )
    const top = rect.bottom + viewportPadding

    setColumnMenuPosition({
      top,
      left,
      maxHeight: Math.max(160, window.innerHeight - top - viewportPadding),
    })
    setOpenColumnMenu(column)
  }

  const handleFilterChange = (column: string, value: string) => {
    setFilters(current => ({ ...current, [column]: value }))
  }

  const toggleChoiceFilter = (column: string, value: string) => {
    setChoiceFilters(current => {
      const selectedValues = current[column] ?? []
      const nextValues = selectedValues.includes(value)
        ? selectedValues.filter(item => item !== value)
        : [...selectedValues, value]

      if (nextValues.length === 0) {
        const next = { ...current }
        delete next[column]
        return next
      }

      return { ...current, [column]: nextValues }
    })
  }

  const updateDateFilter = (column: string, patch: Partial<DateFilterState>) => {
    setDateFilters(current => ({
      ...current,
      [column]: {
        mode: current[column]?.mode ?? 'before',
        from: current[column]?.from ?? '',
        to: current[column]?.to ?? '',
        ...patch,
      },
    }))
  }

  const clearFilter = (column: string) => {
    setFilters(current => {
      const next = { ...current }
      delete next[column]
      return next
    })
    setChoiceFilters(current => {
      const next = { ...current }
      delete next[column]
      return next
    })
    setDateFilters(current => {
      const next = { ...current }
      delete next[column]
      return next
    })
  }

  const clearAllFilters = () => {
    setSearch('')
    setFilters({})
    setChoiceFilters({})
    setDateFilters({})
    setOpenColumnMenu(null)
    setColumnMenuPosition(null)
  }

  const toggleStudioSelection = (selectionKey: string) => {
    setSelectedRowKeys(current =>
      current.includes(selectionKey)
        ? current.filter(key => key !== selectionKey)
        : [...current, selectionKey]
    )
  }

  const toggleVisibleStudioSelection = () => {
    setSelectedRowKeys(current => {
      const visible = new Set(visibleRowKeys)
      if (visible.size === 0) return current
      if (Array.from(visible).every(key => current.includes(key))) {
        return current.filter(key => !visible.has(key))
      }
      return Array.from(new Set([...current, ...visible]))
    })
  }

  const clearStudioSelection = () => {
    setSelectedRowKeys([])
    setInformerDrawerOpen(false)
    setAddOnboardingDrawerOpen(false)
  }

  const handleModeChange = (modeId: OriginalHistoryModeId) => {
    if (modeId === activeModeId) return

    const nextMode = ORIGINAL_HISTORY_MODES.find(mode => mode.id === modeId) ?? ORIGINAL_HISTORY_MODES[0]
    const shouldClearRows = nextMode.rpc !== activeMode.rpc

    setActiveModeId(modeId)
    if (shouldClearRows) setRows([])
    setError(null)
    setSort(null)
    setSearch('')
    setFilters({})
    setChoiceFilters({})
    setDateFilters({})
    setOpenColumnMenu(null)
    setColumnMenuPosition(null)
    setPhotoPeopleByPartner({})
    setPhotoError(null)
    setSelectedRowKeys([])
    setInformerDrawerOpen(false)
    setAddOnboardingDrawerOpen(false)
    setInformerSuccess(null)
  }

  const handleCaseAction = (action: OriginalCaseAction) => {
    if (!canEditOriginalCases) return

    if (action.type === 'onboarding') {
      setOnboardingTarget(action.partnerTarget ?? 'new_studio')
      setOnboardingMode(action.onboardingMode)
      setCasePickerOpen(false)
      return
    }

    if (action.type === 'purchase') {
      setPurchaseMode(action.purchaseMode)
      setCasePickerOpen(false)
      return
    }

    if (action.type === 'state_change') {
      setStateChangeMode(action.stateChangeMode)
      setCasePickerOpen(false)
      return
    }

    if (action.type === 'partner_status') {
      setPartnerStatusMode(action.partnerStatusMode)
      setCasePickerOpen(false)
      return
    }

    if (action.type === 'partner_group') {
      setPartnerGroupMode(action.partnerGroupMode)
      setCasePickerOpen(false)
      return
    }

    if (action.type === 'subscription_model') {
      setSubscriptionModelOpen(true)
      setCasePickerOpen(false)
    }
  }

  const returnToCasePicker = () => {
    if (!canEditOriginalCases) return

    setPurchaseMode(null)
    setOnboardingMode(null)
    setStateChangeMode(null)
    setPartnerStatusMode(null)
    setPartnerGroupMode(null)
    setSubscriptionModelOpen(false)
    setCasePickerOpen(true)
  }

  useEffect(() => {
    loadRows()
  }, [loadRows])

  useEffect(() => {
    if (showStudioSelection) return
    setSelectedRowKeys([])
    setInformerDrawerOpen(false)
    setAddOnboardingDrawerOpen(false)
  }, [showStudioSelection])

  useEffect(() => {
    if (selectedRowKeys.length === 0 || modeRows.length === 0) return

    const availableKeys = new Set(modeRows.map(getRowSelectionKey).filter((value): value is string => value !== null))
    setSelectedRowKeys(current => current.filter(key => availableKeys.has(key)))
  }, [modeRows, selectedRowKeys.length])

  useEffect(() => {
    if (canEditOriginalCases) return
    setCasePickerOpen(false)
    setPurchaseMode(null)
    setOnboardingMode(null)
    setStateChangeMode(null)
    setPartnerStatusMode(null)
    setPartnerGroupMode(null)
    setSubscriptionModelOpen(false)
  }, [canEditOriginalCases])

  useEffect(() => {
    if (!openColumnMenu) return

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Node)) return

      const tableArea = tableAreaRef.current
      if (!tableArea || !tableArea.contains(target)) {
        setOpenColumnMenu(null)
        setColumnMenuPosition(null)
        return
      }

      const element = target instanceof Element ? target : target.parentElement
      if (element?.closest('[data-original-column-menu="true"]')) return
      if (element?.closest('[data-original-column-menu-trigger="true"]')) return

      setOpenColumnMenu(null)
      setColumnMenuPosition(null)
    }

    const closeOnResize = () => {
      setOpenColumnMenu(null)
      setColumnMenuPosition(null)
    }

    document.addEventListener('pointerdown', handlePointerDown)
    window.addEventListener('resize', closeOnResize)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      window.removeEventListener('resize', closeOnResize)
    }
  }, [openColumnMenu])

  useEffect(() => {
    let cancelled = false

    async function loadPhotoPeople() {
      if (activeModeId !== 'partners' || rows.length === 0) {
        setPhotoPeopleByPartner({})
        setPhotoError(null)
        setPhotoLoading(false)
        return
      }

      const partnerNumbers = rows
        .map(getRowPartnerNumber)
        .filter((value): value is number => value !== null)

      if (partnerNumbers.length === 0) {
        setPhotoPeopleByPartner({})
        setPhotoError(null)
        setPhotoLoading(false)
        return
      }

      setPhotoLoading(true)
      setPhotoError(null)

      try {
        const people = await listPartnerPhotoPeople(partnerNumbers)
        if (cancelled) return

        const next = people.reduce<Record<number, PartnerPhotoPerson[]>>((acc, person) => {
          const partnerNumber = Number(person.partner_number)
          acc[partnerNumber] = [...(acc[partnerNumber] ?? []), person]
          return acc
        }, {})

        Object.keys(next).forEach(key => {
          next[Number(key)].sort((left, right) => left.person_id - right.person_id)
        })

        setPhotoPeopleByPartner(next)
      } catch (photoLoadError) {
        if (!cancelled) {
          setPhotoPeopleByPartner({})
          setPhotoError(getErrorMessage(photoLoadError))
        }
      } finally {
        if (!cancelled) setPhotoLoading(false)
      }
    }

    loadPhotoPeople()

    return () => {
      cancelled = true
    }
  }, [activeModeId, rows])

  const handlePhotoUpload = async (person: PartnerPhotoPerson, file: File) => {
    const actionKey = `${person.partner_number}:${person.person_id}`
    setPhotoActionKey(actionKey)
    setPhotoError(null)

    try {
      const nextPerson = await uploadPartnerPersonPhoto({
        partnerNumber: person.partner_number,
        personId: person.person_id,
        file,
      })
      setPhotoPeopleByPartner(current => mergePhotoPerson(current, nextPerson))
    } catch (uploadError) {
      setPhotoError(getErrorMessage(uploadError))
    } finally {
      setPhotoActionKey(current => current === actionKey ? null : current)
    }
  }

  const handlePhotoDelete = async (person: PartnerPhotoPerson) => {
    const path = person.photo_path ?? person.inferred_storage_path
    const name = person.display_name || `${person.first_name} ${person.last_name}`.trim() || `персоны ${person.person_id}`

    if (!path || !window.confirm(`Удалить фото ${name}?`)) return

    const actionKey = `${person.partner_number}:${person.person_id}`
    setPhotoActionKey(actionKey)
    setPhotoError(null)

    try {
      const nextPerson = await deletePartnerPersonPhoto({
        partnerNumber: person.partner_number,
        personId: person.person_id,
        path,
      })
      setPhotoPeopleByPartner(current => mergePhotoPerson(current, nextPerson))
    } catch (deleteError) {
      setPhotoError(getErrorMessage(deleteError))
    } finally {
      setPhotoActionKey(current => current === actionKey ? null : current)
    }
  }

  const handlePhotoLink = async (person: PartnerPhotoPerson) => {
    if (!person.inferred_storage_path) return

    const actionKey = `${person.partner_number}:${person.person_id}`
    setPhotoActionKey(actionKey)
    setPhotoError(null)

    try {
      const nextPerson = await linkPartnerPersonPhoto({
        partnerNumber: person.partner_number,
        personId: person.person_id,
        path: person.inferred_storage_path,
      })
      setPhotoPeopleByPartner(current => mergePhotoPerson(current, nextPerson))
    } catch (linkError) {
      setPhotoError(getErrorMessage(linkError))
    } finally {
      setPhotoActionKey(current => current === actionKey ? null : current)
    }
  }

  return (
    <div className="flex min-h-[calc(100vh-112px)] gap-3 pb-24">
      {purchaseMode && (
        <StudioPurchaseModal
          initialMode={purchaseMode}
          onBack={returnToCasePicker}
          onClose={() => setPurchaseMode(null)}
          onDone={loadRows}
        />
      )}

      {onboardingMode && (
        <StudioOnboardingModal
          initialMode={onboardingMode}
          initialPartnerTarget={onboardingTarget}
          onBack={returnToCasePicker}
          onClose={() => setOnboardingMode(null)}
          onDone={loadRows}
        />
      )}

      {stateChangeMode && (
        <StudioStateChangeModal
          initialMode={stateChangeMode}
          onBack={returnToCasePicker}
          onClose={() => setStateChangeMode(null)}
          onDone={loadRows}
        />
      )}

      {partnerStatusMode && (
        <PartnerStatusModal
          initialMode={partnerStatusMode}
          onBack={returnToCasePicker}
          onClose={() => setPartnerStatusMode(null)}
          onDone={loadRows}
        />
      )}

      {partnerGroupMode && (
        <PartnerGroupMembershipModal
          initialMode={partnerGroupMode}
          onBack={returnToCasePicker}
          onClose={() => setPartnerGroupMode(null)}
          onDone={loadRows}
        />
      )}

      {subscriptionModelOpen && (
        <StudioSubscriptionModelModal
          onBack={returnToCasePicker}
          onClose={() => setSubscriptionModelOpen(false)}
          onDone={loadRows}
        />
      )}

      {informerDrawerOpen && selectedStudios.length > 0 && (
        <OriginalInformerDrawer
          studios={selectedStudios}
          onClose={() => setInformerDrawerOpen(false)}
          onCreated={() => {
            setInformerDrawerOpen(false)
            setSelectedRowKeys([])
            setInformerSuccess('Кейс создан и передан ответственному подразделению.')
          }}
        />
      )}

      {addOnboardingDrawerOpen && (
        <AddOnboardingCaseDrawer
          onClose={() => setAddOnboardingDrawerOpen(false)}
          onCreated={() => {
            setAddOnboardingDrawerOpen(false)
            setInformerSuccess('Кейс создан и передан в кабинет кейсов.')
          }}
        />
      )}

      <aside
        className={`hidden shrink-0 rounded-md border border-border bg-card transition-[width] duration-200 md:flex md:flex-col ${
          sidebarCollapsed ? 'w-16' : 'w-52'
        }`}
      >
        <div className={`flex items-center border-b border-border p-2 ${sidebarCollapsed ? 'justify-center' : 'justify-between gap-2'}`}>
          {!sidebarCollapsed && (
            <div className="min-w-0 px-1">
              <div className="text-sm font-semibold leading-tight text-foreground">Реестр студий и франчайзи</div>
              <div className="truncate text-[11px] text-muted-foreground">Администрирование</div>
            </div>
          )}
          <button
            type="button"
            onClick={() => setSidebarCollapsed(current => !current)}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            title={sidebarCollapsed ? 'Развернуть меню' : 'Свернуть меню'}
            aria-label={sidebarCollapsed ? 'Развернуть меню' : 'Свернуть меню'}
            aria-expanded={!sidebarCollapsed}
          >
            {sidebarCollapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
          </button>
        </div>

        <nav className="flex flex-1 flex-col gap-1 p-2" aria-label="Навигация раздела Реестр студий и франчайзи">
          {ORIGINAL_HISTORY_MODES.map(mode => {
            const Icon = mode.icon
            const isActive = mode.id === activeModeId

            return (
              <button
                key={mode.id}
                type="button"
                onClick={() => handleModeChange(mode.id)}
                title={sidebarCollapsed ? mode.label : undefined}
                className={`group flex h-10 items-center rounded-md border text-sm font-medium transition-colors ${
                  sidebarCollapsed ? 'justify-center px-0' : 'gap-2 px-3 text-left'
                } ${
                  isActive
                    ? 'border-yellow-300 bg-yellow-50 text-yellow-800 shadow-[inset_3px_0_0_#facc15]'
                    : 'border-transparent text-muted-foreground hover:bg-accent hover:text-foreground'
                }`}
              >
                <Icon size={17} className={`shrink-0 ${isActive ? 'text-yellow-600' : 'text-muted-foreground group-hover:text-foreground'}`} />
                {!sidebarCollapsed && <span className="min-w-0 truncate">{mode.label}</span>}
              </button>
            )
          })}
        </nav>
      </aside>

      <main className="min-w-0 flex-1 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <ActiveModeIcon size={20} className="shrink-0 text-primary" />
            <h1 className="truncate text-lg font-semibold text-foreground">{activeMode.label}</h1>
          </div>
          {activeMode.description && (
            <div className="mt-1 text-xs text-muted-foreground">{activeMode.description}</div>
          )}
          <div className="mt-2 flex flex-wrap gap-2 md:hidden">
            {ORIGINAL_HISTORY_MODES.map(mode => {
              const isActive = mode.id === activeModeId

              return (
                <button
                  key={mode.id}
                  type="button"
                  onClick={() => handleModeChange(mode.id)}
                  className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                    isActive
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-card text-muted-foreground hover:bg-accent hover:text-foreground'
                  }`}
                >
                  {mode.label}
                </button>
              )
            })}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {loading
              ? 'Загружаем строки'
              : `Строк: ${sortedRows.length.toLocaleString('ru-RU')} из ${modeRows.length.toLocaleString('ru-RU')}`}
            {filterCount > 0 ? ` · фильтров: ${filterCount}` : ''}
            {sort ? ` · сортировка: ${sort.column}, ${sort.direction === 'asc' ? 'по возрастанию' : 'по убыванию'}` : ''}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setOnboardingMode('partner')}
            className="hidden h-9 items-center gap-1 rounded-md border border-border bg-card px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            title="Завести франчайзи, студию, присвоить номер и адрес"
          >
            <UserPlus size={14} />
            Франчайзи и студии
          </button>
          <button
            type="button"
            onClick={() => setPurchaseMode('plan')}
            className="hidden h-9 items-center gap-1 rounded-md border border-border bg-card px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            title="Запланировать покупку студии (владение BUYING)"
          >
            <ShoppingCart size={14} />
            План покупки
          </button>
          <button
            type="button"
            onClick={() => setPurchaseMode('complete')}
            className="hidden h-9 items-center gap-1 rounded-md border border-border bg-card px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            title="Провести покупку студии (передача владения)"
          >
            <ShoppingCart size={14} />
            Провести покупку
          </button>
          {canEditOriginalCases && (
            <button
              type="button"
              onClick={() => setCasePickerOpen(true)}
              className="inline-flex h-9 items-center gap-1 rounded-md border border-primary bg-primary px-3 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              <ClipboardList size={14} />
              Внести изменения
            </button>
          )}
          {showStudioSelection && (
            <button
              type="button"
              onClick={() => setAddOnboardingDrawerOpen(true)}
              className="inline-flex h-9 items-center gap-1 rounded-md border border-border bg-card px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <Plus size={14} />
              Добавить
            </button>
          )}
          {filterCount > 0 && (
            <button
              type="button"
              onClick={clearAllFilters}
              className="inline-flex h-9 items-center gap-1 rounded-md border border-border bg-card px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <X size={14} />
              Сбросить
            </button>
          )}
          <button
            type="button"
            onClick={loadRows}
            disabled={loading}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-card text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-60"
            title="Обновить"
            aria-label="Обновить"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {!isStatisticsMode && (
        <div className="relative max-w-xl">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder={searchPlaceholder}
            className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm"
            aria-label="Поиск по реестру"
          />
        </div>
      )}

      {canEditOriginalCases && casePickerOpen && (
        <OriginalCasesPanel
          selectedCategoryTitle={selectedCaseCategory}
          onCategoryChange={setSelectedCaseCategory}
          onAction={handleCaseAction}
          onClose={() => setCasePickerOpen(false)}
        />
      )}

      {informerSuccess && (
        <div className="flex items-start justify-between gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
          <div>{informerSuccess}</div>
          <button
            type="button"
            onClick={() => setInformerSuccess(null)}
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-emerald-700 hover:bg-emerald-100"
            aria-label="Скрыть сообщение"
            title="Скрыть сообщение"
          >
            <X size={13} />
          </button>
        </div>
      )}

      {error ? (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle size={18} className="mt-0.5 shrink-0" />
          <div>{error}</div>
        </div>
      ) : (
        <div className="space-y-2">
          {showPartnerPhotos && photoError && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle size={18} className="mt-0.5 shrink-0" />
              <div>{photoError}</div>
            </div>
          )}

          {showPartnerPhotos && (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
              <Camera size={15} />
              <span>Аватарки франчайзи</span>
              {photoLoading && <Loader2 size={14} className="animate-spin" />}
            </div>
          )}

          {showStudioSelection && selectedStudios.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-primary/25 bg-primary/5 px-3 py-2">
              <div className="text-sm font-medium text-foreground">
                Выбрано: {selectedStudios.length.toLocaleString('ru-RU')}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setInformerDrawerOpen(true)}
                  className="inline-flex h-9 items-center gap-2 rounded-md border border-primary bg-primary px-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  <ClipboardList size={15} />
                  Передать информацию
                </button>
                <button
                  type="button"
                  onClick={clearStudioSelection}
                  className="h-9 rounded-md border border-border bg-card px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  Снять выбор
                </button>
              </div>
            </div>
          )}

        {isStatisticsMode ? (
          <StatisticsDashboard rows={modeRows} gradeRows={gradeStatsRows} specialCaseRows={specialCaseRows} countryRows={countryRows} />
        ) : (
        <div ref={tableAreaRef} className="rounded-md border border-border bg-card">
          <div
            className={`${isStatisticsMode ? 'max-h-[560px] min-h-0' : 'min-h-[320px] max-h-[calc(100vh-220px)]'} overflow-auto`}
            onScroll={() => {
              setOpenColumnMenu(null)
              setColumnMenuPosition(null)
            }}
          >
            <table className={`${isStatisticsMode ? 'w-auto min-w-[520px] text-xs' : 'min-w-max w-full text-sm'} border-separate border-spacing-0`}>
              <thead>
                <tr className="bg-muted">
                  {showStudioSelection && (
                    <th className="sticky left-0 top-0 z-30 w-[46px] min-w-[46px] border-b border-r border-border bg-muted px-2 py-2 text-center align-middle">
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        disabled={visibleRowKeys.length === 0}
                        onChange={toggleVisibleStudioSelection}
                        className="h-4 w-4 rounded border-border"
                        aria-label="Выбрать видимые студии"
                        title="Выбрать видимые студии"
                      />
                    </th>
                  )}
                  {showPartnerPhotos && (
                    <th className="sticky left-0 top-0 z-30 w-[118px] min-w-[118px] border-b border-r border-border bg-muted px-2 py-2 text-left align-bottom text-xs font-semibold text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <ImageIcon size={14} />
                        Аватарки
                      </div>
                    </th>
                  )}
                  {columns.map((column, columnIndex) => {
                    const isSorted = sort?.column === column
                    const selectedChoiceValues = choiceFilters[column] ?? []
                    const dateFilter = dateFilters[column]
                    const hasDateFilter = isDateFilterActive(dateFilter)
                    const hasFilter = Boolean(filters[column]?.trim()) || selectedChoiceValues.length > 0 || hasDateFilter
                    const isChoiceFilter = isChoiceFilterColumn(column)
                    const isDateFilter = isDateColumn(column)
                    const choiceOptions = choiceFilterOptions[column] ?? []
                    const isMenuOpen = openColumnMenu === column
                    const widthClass = getColumnWidthClass(column)

                    return (
                      <th
                        key={column}
                        className={`sticky top-0 z-20 border-b border-r border-border bg-muted text-left align-bottom text-xs font-semibold text-muted-foreground last:border-r-0 ${isStatisticsMode ? 'px-2 py-1.5' : 'px-3 py-2'} ${widthClass}`}
                      >
                        <div className="relative">
                          <button
                            type="button"
                            data-original-column-menu-trigger="true"
                            onClick={event => toggleColumnMenu(column, event.currentTarget, columnIndex < 2)}
                            className="flex w-full items-center justify-between gap-2 text-left transition-colors hover:text-foreground"
                            title={`Сортировка и фильтр: ${column}`}
                          >
                            <span className="break-words leading-tight">{column}</span>
                            <span className="flex shrink-0 items-center gap-1">
                              <Filter size={14} className={hasFilter || isSorted ? 'text-primary' : 'opacity-50'} />
                            </span>
                          </button>

                          {isMenuOpen && (
                              <div
                                data-original-column-menu="true"
                                className="fixed z-50 w-64 overflow-y-auto rounded-md border border-border bg-card p-2 text-foreground shadow-xl"
                                style={columnMenuPosition ?? undefined}
                              >
                              <div className="mb-2 truncate text-xs font-semibold text-foreground">{column}</div>
                              <div className="mb-1 text-[11px] font-semibold text-muted-foreground">Сортировать</div>
                              <div className="grid grid-cols-2 gap-2">
                                <button
                                  type="button"
                                  onClick={() => setColumnSort(column, 'asc')}
                                  className={`rounded-md border bg-card px-2 py-1.5 text-xs transition-colors hover:bg-accent ${isSorted && sort.direction === 'asc' ? 'border-primary text-primary' : 'border-border text-muted-foreground'}`}
                                >
                                  А-Я
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setColumnSort(column, 'desc')}
                                  className={`rounded-md border bg-card px-2 py-1.5 text-xs transition-colors hover:bg-accent ${isSorted && sort.direction === 'desc' ? 'border-primary text-primary' : 'border-border text-muted-foreground'}`}
                                >
                                  Я-А
                                </button>
                              </div>
                              <div className="mt-2 mb-1 text-[11px] font-semibold text-muted-foreground">Фильтровать</div>
                              {isChoiceFilter ? (
                                <div className="mt-2 max-h-56 overflow-y-auto rounded-md border border-border bg-card p-1">
                                  {choiceOptions.length === 0 ? (
                                    <div className="px-2 py-2 text-xs text-muted-foreground">Нет значений</div>
                                  ) : (
                                    choiceOptions.map(option => {
                                      const checked = selectedChoiceValues.includes(option)

                                      return (
                                        <label
                                          key={option}
                                          className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-accent"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggleChoiceFilter(column, option)}
                                            className="h-3.5 w-3.5 rounded border-border"
                                          />
                                          <span className="min-w-0 truncate" title={option}>{option}</span>
                                        </label>
                                      )
                                    })
                                  )}
                                  {hasFilter && (
                                    <button
                                      type="button"
                                      onClick={() => clearFilter(column)}
                                      className="mt-1 w-full rounded px-2 py-1.5 text-left text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
                                    >
                                      Снять фильтр
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <div className="mt-2 space-y-2">
                                  <div className="flex h-9 items-center gap-1 rounded-md border border-border bg-card px-2 focus-within:ring-1 focus-within:ring-primary">
                                    <input
                                      value={filters[column] ?? ''}
                                      onChange={event => handleFilterChange(column, event.target.value)}
                                      className="min-w-0 flex-1 bg-transparent text-xs font-normal outline-none placeholder:text-muted-foreground"
                                      placeholder="Фильтр по столбцу"
                                      aria-label={`Фильтр: ${column}`}
                                      autoFocus
                                    />
                                    {hasFilter && (
                                      <button
                                        type="button"
                                        onClick={() => clearFilter(column)}
                                        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
                                        title={`Снять фильтр: ${column}`}
                                        aria-label={`Снять фильтр: ${column}`}
                                      >
                                        <X size={12} />
                                      </button>
                                    )}
                                  </div>

                                  {isDateFilter && (
                                    <div className="space-y-2 rounded-md border border-border bg-muted/30 p-2">
                                      <select
                                        value={dateFilter?.mode ?? ''}
                                        onChange={event => updateDateFilter(column, { mode: event.target.value as DateFilterMode })}
                                        className="h-8 w-full rounded-md border border-border bg-background px-2 text-xs font-normal outline-none focus:ring-1 focus:ring-primary"
                                        aria-label={`Режим фильтра по дате: ${column}`}
                                      >
                                        <option value="" disabled>Выберите период</option>
                                        <option value="before">Дата до</option>
                                        <option value="after">Дата после</option>
                                        <option value="between">Между датами</option>
                                      </select>

                                      {dateFilter?.mode === 'before' && (
                                        <input
                                          type="date"
                                          value={dateFilter.to}
                                          onChange={event => updateDateFilter(column, { to: event.target.value })}
                                          className="h-8 w-full rounded-md border border-border bg-background px-2 text-xs font-normal outline-none focus:ring-1 focus:ring-primary"
                                          aria-label={`Дата до: ${column}`}
                                        />
                                      )}
                                      {dateFilter?.mode === 'after' && (
                                        <input
                                          type="date"
                                          value={dateFilter.from}
                                          onChange={event => updateDateFilter(column, { from: event.target.value })}
                                          className="h-8 w-full rounded-md border border-border bg-background px-2 text-xs font-normal outline-none focus:ring-1 focus:ring-primary"
                                          aria-label={`Дата после: ${column}`}
                                        />
                                      )}
                                      {dateFilter?.mode === 'between' && (
                                        <div className="space-y-2">
                                          <input
                                            type="date"
                                            value={dateFilter.from}
                                            max={dateFilter.to || undefined}
                                            onChange={event => updateDateFilter(column, { from: event.target.value })}
                                            className="h-8 w-full rounded-md border border-border bg-background px-2 text-xs font-normal outline-none focus:ring-1 focus:ring-primary"
                                            aria-label={`Дата от: ${column}`}
                                          />
                                          <input
                                            type="date"
                                            value={dateFilter.to}
                                            min={dateFilter.from || undefined}
                                            onChange={event => updateDateFilter(column, { to: event.target.value })}
                                            className="h-8 w-full rounded-md border border-border bg-background px-2 text-xs font-normal outline-none focus:ring-1 focus:ring-primary"
                                            aria-label={`Дата до: ${column}`}
                                          />
                                        </div>
                                      )}

                                      <div className="text-[10px] font-normal text-muted-foreground">Границы включительно</div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={Math.max(columns.length + (showPartnerPhotos ? 1 : 0) + (showStudioSelection ? 1 : 0), 1)} className={`${isStatisticsMode ? 'h-16' : 'h-24'} px-4 text-center text-muted-foreground`}>
                      Загружаем данные...
                    </td>
                  </tr>
                ) : sortedRows.length === 0 ? (
                  <tr>
                    <td colSpan={Math.max(columns.length + (showPartnerPhotos ? 1 : 0) + (showStudioSelection ? 1 : 0), 1)} className={`${isStatisticsMode ? 'h-16' : 'h-24'} px-4 text-center text-muted-foreground`}>
                      Нет данных для отображения
                    </td>
                  </tr>
                ) : sortedRows.map((row, rowIndex) => (
                  <tr key={rowIndex} className="odd:bg-background/30 hover:bg-accent/40">
                    {showStudioSelection && (
                      <td className="sticky left-0 z-10 w-[46px] min-w-[46px] border-b border-r border-border bg-card px-2 py-2 text-center align-top shadow-[1px_0_0_0_hsl(var(--border))] odd:bg-background">
                        {(() => {
                          const selectionKey = getRowSelectionKey(row)
                          if (!selectionKey) return null

                          return (
                            <input
                              type="checkbox"
                              checked={selectedRowKeys.includes(selectionKey)}
                              onChange={() => toggleStudioSelection(selectionKey)}
                              className="h-4 w-4 rounded border-border"
                              aria-label={`Выбрать студию ${formatCellValue(getCellValue(row, ORIGINAL_COLUMN.studioNumber), ORIGINAL_COLUMN.studioNumber)}`}
                            />
                          )
                        })()}
                      </td>
                    )}
                    {showPartnerPhotos && (
                      <td className="sticky left-0 z-10 w-[118px] min-w-[118px] border-b border-r border-border bg-card px-2 py-1.5 align-top text-left shadow-[1px_0_0_0_hsl(var(--border))] odd:bg-background">
                        <PartnerPhotoCell
                          partnerNumber={getRowPartnerNumber(row)}
                          people={photoPeopleByPartner[getRowPartnerNumber(row) ?? -1] ?? []}
                          loading={photoLoading}
                          actionKey={photoActionKey}
                          onUpload={handlePhotoUpload}
                          onDelete={handlePhotoDelete}
                          onLink={handlePhotoLink}
                        />
                      </td>
                    )}
                    {columns.map(column => (
                      <td
                        key={`${rowIndex}-${column}`}
                        className={`border-b border-r border-border align-top text-foreground last:border-r-0 ${isStatisticsMode ? 'px-2 py-1.5' : 'px-3 py-2'} ${getColumnWidthClass(column)} ${numericColumns.has(column) ? 'text-right tabular-nums' : 'text-left'}`}
                      >
                        <span className="block break-words leading-snug whitespace-normal">
                          {formatCellValue(getCellValue(row, column), column)}
                        </span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}
        </div>
      )}
      </main>
    </div>
  )
}
